import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Bot, CheckCircle2, Copy, ExternalLink, HelpCircle, MessageSquareText, QrCode, RefreshCw, Save, Settings2, ShieldCheck, ShoppingBag, TestTube2, Webhook, WalletCards } from "lucide-react";
import { type AiModelDiscoveryResponse, type AiProvider, type CredentialMetadata, type PlatformAction, type PlatformApplicationConfigMetadata, type PlatformApplicationProvider, type ServiceConfigEnableInput, type SessionView } from "@control-plane/contracts";
import { PageHeader } from "../components/PageHeader";
import { RemotePageState } from "../components/RemotePageState";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { V3PageTabs, type V3PageTab } from "../components/V3PageTabs";
import { Dialog as V3Dialog } from "../components/Overlay";
import { V3Select } from "../components/V3Select";
import { InlineFeedback } from "../components/Feedback";
import { ApprovalGateModal, credentialRotateApprovalPayload, type ApprovalOperation } from "../components/ApprovalGate";
import { ApiClientError, api } from "../lib/api";
import { businessLabel } from "../lib/displayNames";
import { useRemote } from "../lib/useRemote";
import { loadServiceConfigCenter, type AiCatalogModel, type AiCatalogService, type AiModelCapability, type ServiceCheckStatus, type ServiceConfigCard, type ServiceConfigCenter, type ServiceConfigSection, type ServiceSubItem } from "../lib/serviceConfig";
import { AiOcrConfigPanel } from "./AiOcrConfigPanel";

export type { ServiceConfigSection } from "../lib/serviceConfig";

type MetadataResponse<T> = { items: T[] };
type AssistantDefault = { provider: AiProvider; model: string };
type PageData = {
  center: ServiceConfigCenter;
  platformConfigs: PlatformApplicationConfigMetadata[];
  credentials: CredentialMetadata[];
  assistantDefault: AssistantDefault | null;
  rollbackWindows: CredentialRollbackWindow[];
  rollbackWindowsUnavailable: boolean;
};
type CredentialRollbackWindow = {
  id: string;
  credentialId: string;
  tenantId: string | null;
  provider: string;
  name: string;
  fromVersion: number;
  toVersion: number;
  status: "open" | "executed" | "expired" | "cancelled";
  reason: string;
  openedAt: string;
  expiresAt: string;
  executedAt: string | null;
};
type RollbackWindowResponse = { items: CredentialRollbackWindow[] };
type SectionDefinition = { icon: LucideIcon; label: string; shortLabel: string; purpose: string };
type SecretField = { key: string; label: string; placeholder: string; optional?: boolean };
type PlatformDefinition = {
  provider: PlatformApplicationProvider;
  title: string;
  description: string;
  callbackLabel: string;
  fields: SecretField[];
  logo: string;
  secondaryLogo?: string;
  officialUrl: string;
  operatorSteps: string[];
};
type AiProviderDefinition = { provider: AiProvider; title: string; vendor: string; recommendedBaseUrl: string; baseUrlPlaceholder: string };
type ServiceActionResult = { status: ServiceCheckStatus; title: string; detail: string; checkedAt?: string | null };
type RawVerificationResponse = { status?: "success" | "failed" | "unsupported" | ServiceCheckStatus; checkedAt?: string | null; errorCode?: string | null; balanceStatus?: string | null; detail?: string | null; message?: string | null };
type ServiceApprovalRequest = {
  operation: ApprovalOperation;
  title: string;
  targetLabel: string;
  impact: string[];
  recovery: string;
  execute: (approvalId: string, reason: string) => Promise<void>;
};
type RequestApproval = (request: ServiceApprovalRequest) => void;

const sectionOrder: ServiceConfigSection[] = ["platform", "ai", "sms"];
const sectionDefinitions: Record<ServiceConfigSection, SectionDefinition> = {
  platform: { icon: ShoppingBag, label: "电商平台应用", shortLabel: "平台应用", purpose: "各平台应用资料、官方授权回调和消息回调统一配置。" },
  ai: { icon: Bot, label: "AI 模型服务", shortLabel: "AI", purpose: "模型账号、服务端模型目录、连通测试、预算和降级证据统一管理。" },
  sms: { icon: MessageSquareText, label: "登录与短信", shortLabel: "短信", purpose: "验证码短信、微信扫码登录、真实发送测试和回执验证统一管理。" },
};
const serviceConfigTabs: V3PageTab[] = sectionOrder.map((section) => ({
  value: section,
  label: sectionDefinitions[section].shortLabel,
  icon: sectionDefinitions[section].icon,
  panelId: `service-config-panel-${section}`,
}));

const platformDefinitions: PlatformDefinition[] = [
  {
    provider: "douyin",
    title: "抖店开放平台",
    description: "用于商家从官方服务市场授权给 ERP，并让系统安全调用抖店接口。",
    callbackLabel: "官方授权返回地址",
    fields: [
      { key: "serviceId", label: "服务市场编号", placeholder: "从抖店开放平台复制 Service ID" },
      { key: "appKey", label: "应用编号", placeholder: "从抖店开放平台复制 App Key" },
      { key: "appSecret", label: "应用密钥", placeholder: "从抖店开放平台复制 App Secret" },
    ],
    logo: "/platform/douyin-shop.png",
    officialUrl: "https://op.jinritemai.com/",
    operatorSteps: ["工具型应用与服务发布审核通过", "商家已订购服务且处于有效期", "开放平台登记应用使用地址并开通所需 API 权限"],
  },
  {
    provider: "xhs",
    title: "小红书开放平台",
    description: "用于商家授权小红书店铺，并让 ERP 安全调用官方电商接口。",
    callbackLabel: "官方授权返回地址",
    fields: [
      { key: "appId", label: "应用编号", placeholder: "从小红书开放平台复制 App ID / App Key" },
      { key: "appSecret", label: "应用密钥", placeholder: "从小红书开放平台复制 App Secret" },
    ],
    logo: "/platform/xiaohongshu.png",
    officialUrl: "https://ark.xiaohongshu.com/",
    operatorSteps: ["服务商应用审核并取得生产凭据", "在平台后台登记精确回调地址", "完成沙箱/联调与正式 API 权限开通"],
  },
  {
    provider: "taobao",
    title: "淘宝 / 天猫开放平台",
    description: "用于淘宝/天猫商家授权，并让 ERP 安全调用订单、商品、库存等官方接口。",
    callbackLabel: "官方授权返回地址",
    fields: [
      { key: "appKey", label: "应用编号", placeholder: "从淘宝开放平台复制 App Key / Client ID" },
      { key: "appSecret", label: "应用密钥", placeholder: "从淘宝开放平台复制 App Secret" },
    ],
    logo: "/platform/taobao.png",
    secondaryLogo: "/platform/tmall.png",
    officialUrl: "https://open.taobao.com/",
    operatorSteps: ["应用创建与类目/资质审核通过", "授权回调地址与当前生产地址一致", "申请订单、商品、库存、物流等实际 API 权限"],
  },
  {
    provider: "jd",
    title: "京东宙斯开放平台",
    description: "用于京东商家授权，以及后续订单、商品、库存和售后能力接入。",
    callbackLabel: "授权回调地址",
    fields: [
      { key: "appKey", label: "应用编号", placeholder: "从京东宙斯开放平台复制 App Key" },
      { key: "appSecret", label: "应用密钥", placeholder: "从京东宙斯开放平台复制 App Secret" },
    ],
    logo: "/platform/jd.png",
    officialUrl: "https://jos.jd.com/",
    operatorSteps: ["在京东宙斯开放平台创建应用并完成资质审核", "申请业务所需权限包", "登记本系统生成的真实授权回调地址"],
  },
  {
    provider: "alibaba_1688",
    title: "1688 开放平台",
    description: "用于 1688 商家授权，以及采购、商品和订单能力接入。",
    callbackLabel: "授权回调地址",
    fields: [
      { key: "appKey", label: "应用编号", placeholder: "从 1688 开放平台复制 App Key" },
      { key: "appSecret", label: "应用密钥", placeholder: "从 1688 开放平台复制 App Secret" },
    ],
    logo: "/platform/1688.png",
    officialUrl: "https://open.1688.com/",
    operatorSteps: ["在 1688 开放平台创建应用并完成审核", "申请采购、商品或订单所需接口权限", "登记本系统生成的真实授权回调地址"],
  },
  {
    provider: "pdd",
    title: "拼多多开放平台",
    description: "用于拼多多商家授权，并让 ERP 安全调用拼多多官方接口。",
    callbackLabel: "官方授权返回地址",
    fields: [
      { key: "clientId", label: "应用编号", placeholder: "从拼多多开放平台复制 Client ID" },
      { key: "clientSecret", label: "应用密钥", placeholder: "从拼多多开放平台复制 Client Secret" },
    ],
    logo: "/platform/pdd.png",
    officialUrl: "https://open.pinduoduo.com/",
    operatorSteps: ["服务商应用与服务市场商品审核通过", "平台登记的 redirect_uri 精确匹配", "商家订购并授权所需接口范围"],
  },
  {
    provider: "kuaishou",
    title: "快手电商服务商",
    description: "用于快手商家授权、接口签名和消息校验。",
    callbackLabel: "官方授权返回地址",
    fields: [
      { key: "appKey", label: "应用编号", placeholder: "从快手开放平台复制 App Key" },
      { key: "appSecret", label: "应用密钥", placeholder: "从快手开放平台复制 App Secret" },
      { key: "signSecret", label: "签名密钥", placeholder: "从快手开放平台复制 Sign Secret" },
      { key: "messageSecret", label: "消息密钥", placeholder: "从快手开放平台复制 Message Secret", optional: true },
    ],
    logo: "/platform/kuaishou.png",
    officialUrl: "https://open.kwaixiaodian.com/",
    operatorSteps: ["电商服务商应用审核通过", "登记回调地址并申请所需 merchant scopes", "商家订购服务并完成授权"],
  },
  {
    provider: "wechat_shop",
    title: "微信小店",
    description: "用于微信小店消息校验，以及后续多商户授权。",
    callbackLabel: "多商户授权返回地址（预留）",
    fields: [
      { key: "appId", label: "应用编号", placeholder: "从微信小店后台复制 AppID" },
      { key: "messageToken", label: "消息 Token", placeholder: "WECHAT_SHOP_MESSAGE_TOKEN" },
      { key: "messageAesKey", label: "消息加解密密钥", placeholder: "微信提供的 43 位 EncodingAESKey" },
      { key: "openComponentAppId", label: "第三方平台应用编号", placeholder: "从微信开放平台复制第三方平台 AppID", optional: true },
      { key: "openComponentAppSecret", label: "第三方平台应用密钥", placeholder: "从微信开放平台复制第三方平台 AppSecret", optional: true },
    ],
    logo: "/platform/wechat-shop.png",
    officialUrl: "https://open.weixin.qq.com/",
    operatorSteps: ["当前三项必填仅完成单店消息校验配置", "多商户服务商还需微信开放平台第三方平台审核", "官方授权票据、预授权码和授权方访问令牌链路完成前不会标记为已连接"],
  },
];

const aiProviderDefinitions: AiProviderDefinition[] = [
  { provider: "qwen", title: "通义千问", vendor: "阿里云百炼", recommendedBaseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1", baseUrlPlaceholder: "填写服务商控制台提供的 HTTPS 接口地址" },
  { provider: "deepseek", title: "DeepSeek", vendor: "DeepSeek", recommendedBaseUrl: "https://api.deepseek.com", baseUrlPlaceholder: "填写服务商控制台提供的 HTTPS 接口地址" },
  { provider: "kimi", title: "Kimi", vendor: "Moonshot AI", recommendedBaseUrl: "https://api.moonshot.cn/v1", baseUrlPlaceholder: "填写服务商控制台提供的 HTTPS 接口地址" },
  { provider: "zhipu", title: "智谱 GLM", vendor: "智谱 AI · 聊天模型服务", recommendedBaseUrl: "https://open.bigmodel.cn/api/paas/v4", baseUrlPlaceholder: "填写服务商控制台提供的 HTTPS 接口地址" },
  { provider: "mimo", title: "MiMo", vendor: "小米 MiMo", recommendedBaseUrl: "https://api.mimo.ai/v1", baseUrlPlaceholder: "填写服务商控制台提供的 HTTPS 接口地址" },
  { provider: "custom_openai_compatible", title: "自定义兼容模型", vendor: "受控公网 HTTPS 模型托管平台", recommendedBaseUrl: "", baseUrlPlaceholder: "填写模型托管平台提供的公网 HTTPS 接口地址" },
];

const smsDefaults = { provider: "aliyun", regionId: "cn-hangzhou", endpoint: "dysmsapi.aliyuncs.com" };
const rollbackProviderLabels: Record<string, string> = {
  qwen: "通义千问",
  deepseek: "DeepSeek",
  kimi: "Kimi",
  zhipu: "智谱 GLM",
  mimo: "MiMo",
  custom_openai_compatible: "自定义兼容模型",
  "ai-runtime": "AI 默认路线",
  sms: "验证码短信",
  "web-wechat-login": "微信扫码登录",
  douyin: "抖店开放平台",
  taobao: "淘宝 / 天猫开放平台",
  alibaba_1688: "1688 开放平台",
  jd: "京东宙斯开放平台",
  pdd: "拼多多开放平台",
  xhs: "小红书开放平台",
  kuaishou: "快手电商服务商",
  wechat_shop: "微信小店",
};
const rollbackCredentialNameLabels: Record<string, string> = {
  "ai-provider-runtime": "AI 模型账号",
  "ai-agent-routing": "AI 默认路线",
  "sms-runtime": "验证码短信配置",
  "web-wechat-runtime": "微信扫码登录配置",
  "platform-application": "平台应用配置",
};

function isServiceConfigSection(value: string | null | undefined): value is ServiceConfigSection {
  return value === "platform" || value === "ai" || value === "sms";
}

function initialSectionFromLocation(initialSection?: ServiceConfigSection): ServiceConfigSection {
  if (initialSection) return initialSection;
  if (typeof window === "undefined") return "platform";
  const querySection = new URLSearchParams(window.location.search).get("section");
  return isServiceConfigSection(querySection) ? querySection : "platform";
}

function hasAction(session: SessionView, action: PlatformAction): boolean {
  return session.actions.includes(action);
}

function canManageServiceConfig(session: SessionView): boolean {
  return (session.admin.role === "platform_owner" || session.admin.role === "security_admin") && hasAction(session, "credential.write");
}

function canRotateCredential(session: SessionView): boolean {
  return canManageServiceConfig(session) && hasAction(session, "credential.rotate");
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "暂无更新";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "时间待确认";
  return date.toLocaleString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function statusLine(metadata: { updatedAt?: string | null; configuredFields?: string[] } | CredentialMetadata | undefined, configuredParts?: number) {
  if (!metadata) return "必填信息未保存";
  const count = configuredParts ?? ("configuredFields" in metadata && Array.isArray(metadata.configuredFields) ? metadata.configuredFields.length : undefined);
  return `${count ? `已保存 ${count} 项` : "已加密保存"} · 最近更新 ${formatDateTime(metadata.updatedAt)}`;
}

function saveErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiClientError && [404, 501].includes(error.status)) return "服务端能力尚未接入，本次操作没有保存成功。";
  const message = error instanceof Error ? error.message : fallback;
  if (!message.trim()) return fallback;
  if (/fingerprint|activeVersion|credentialId|provider|signingSecret|JSON|stack|trace/i.test(message)) return fallback;
  return message;
}

function makeActionKey(prefix: string) {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${suffix}`;
}

function actionStatus(status: RawVerificationResponse["status"]): ServiceCheckStatus {
  if (status === "success" || status === "verified") return "verified";
  if (status === "failed") return "failed";
  return "unverified";
}

function actionResult(response: RawVerificationResponse, title: string): ServiceActionResult {
  const status = actionStatus(response.status);
  const knownDetail = response.errorCode === "CALLBACK_PROVIDER_EVENT_REQUIRED"
    ? "回调地址可以访问；尚未收到该平台发出的真实授权回调。已有接口连通状态不会因此失效。"
    : null;
  return {
    status,
    title,
    detail: response.detail || response.message || knownDetail || response.balanceStatus || (status === "verified" ? "服务端返回真实验证通过。" : "本次验证尚未取得平台回调凭证。"),
    checkedAt: response.checkedAt ?? null,
  };
}

function failedActionResult(error: unknown, fallback: string): ServiceActionResult {
  return { status: "failed", title: "操作未完成", detail: saveErrorMessage(error, fallback), checkedAt: null };
}

function ActionResultView({ result, empty }: { result: ServiceActionResult | null; empty: string }) {
  if (!result) return <InlineFeedback tone="info">{empty}</InlineFeedback>;
  return <div className="service-step-result">
    <StatusBadge value={result.status} />
    <div>
      <strong>{result.title}</strong>
      <span>{result.detail}</span>
      {result.checkedAt && <small className="app-number">{formatDateTime(result.checkedAt)}</small>}
    </div>
  </div>;
}

async function loadRollbackWindows(canLoadRollbackWindows: boolean) {
  if (!canLoadRollbackWindows) return { items: [] as CredentialRollbackWindow[], unavailable: false };
  try {
    const response = await api<RollbackWindowResponse>("/api/credentials/rollback-windows");
    return { items: response.items, unavailable: false };
  } catch (error) {
    if (error instanceof ApiClientError && [404, 501].includes(error.status)) return { items: [] as CredentialRollbackWindow[], unavailable: true };
    throw error;
  }
}

async function loadAssistantDefault(): Promise<AssistantDefault | null> {
  try {
    return await api<AssistantDefault | null>("/api/assistant/default");
  } catch (error) {
    if (error instanceof ApiClientError && (
      [404, 501].includes(error.status)
      || error.status === 409 && (error.code.startsWith("AI_") || error.code.startsWith("ASSISTANT_"))
    )) return null;
    throw error;
  }
}

async function loadPageData(canLoadSensitiveMetadata: boolean, canLoadRollbackWindows = false): Promise<PageData> {
  if (!canLoadSensitiveMetadata) {
    return {
      center: await loadServiceConfigCenter({ allowLegacyFallback: false }),
      platformConfigs: [],
      credentials: [],
      assistantDefault: null,
      rollbackWindows: [],
      rollbackWindowsUnavailable: false,
    };
  }
  const [center, platformConfigs, credentials, assistantDefault, rollbackWindows] = await Promise.all([
    loadServiceConfigCenter(),
    api<MetadataResponse<PlatformApplicationConfigMetadata>>("/api/platform-app-configs"),
    api<MetadataResponse<CredentialMetadata>>("/api/credentials?scope=platform"),
    loadAssistantDefault(),
    loadRollbackWindows(canLoadRollbackWindows),
  ]);
  return { center, platformConfigs: platformConfigs.items, credentials: credentials.items, assistantDefault, rollbackWindows: rollbackWindows.items, rollbackWindowsUnavailable: rollbackWindows.unavailable };
}

function CopyableReadonlyField({ label, value, disabled = false }: { label: string; value: string; disabled?: boolean }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  async function copy() {
    if (!value || disabled) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("CLIPBOARD_UNAVAILABLE");
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setCopyError("");
    } catch {
      setCopied(false);
      setCopyError("复制失败，请手动选中地址复制，或检查浏览器剪贴板权限。");
    }
  }
  return <label className="full">{label}
    <span className="readonly-copy-field">
      <textarea readOnly rows={2} value={value} onFocus={(event) => event.currentTarget.select()} aria-label={label} />
      <button type="button" className="button secondary compact" disabled={disabled || !value} onClick={() => { void copy(); }}><Copy size={13} />复制</button>
    </span>
    <small className={copyError ? "field-hint danger-text" : "field-hint"} aria-live="polite">{copyError || (copied ? "已复制，请粘贴到对应官方后台核对。" : "系统提供的完整地址，只读不可手填。")}</small>
  </label>;
}

function SecretSavedHelp({ title, mode = "partial" }: { title: string; mode?: "partial" | "full" }) {
  const [open, setOpen] = useState(false);
  return <span className="saved-secret-help">
    <button
      type="button"
      className="secret-help-button"
      aria-label={`${title} 已保存说明`}
      aria-expanded={open}
      onClick={() => setOpen((value) => !value)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    ><HelpCircle size={14} aria-hidden /></button>
    <span className={`saved-secret-popover ${open ? "open" : ""}`} role="tooltip">
      <strong>{title} 已保存</strong>
      <span>{mode === "partial"
        ? "当前内容已经写入加密保险库，页面不显示明文。留空会保持当前值；需要更新时填写新值后再保存。"
        : "当前内容已经写入加密保险库，页面不显示明文。更新或轮换时请重新填写完整参数后再保存。"}</span>
    </span>
  </span>;
}

function ServiceLogo({ item, fallback: Fallback }: { item: ServiceSubItem; fallback: LucideIcon }) {
  return item.logoPath
    ? <img className="service-config-logo" src={item.logoPath} alt={item.title} loading="lazy" />
    : <span className="service-config-logo fallback"><Fallback size={18} aria-hidden /></span>;
}

function checkStatusText(status: ServiceCheckStatus | undefined): string {
  if (status === "verified") return "已验证";
  if (status === "failed") return "失败";
  return "未验证";
}

function serviceStatusText(status: ServiceConfigCard["status"]) {
  if (status === "verified") return "已启用";
  if (status === "configured") return "配置已保存";
  if (status === "failed") return "配置阻断";
  return "未配置";
}

function serviceStatusTone(status: ServiceConfigCard["status"]) {
  if (status === "verified") return "success";
  if (status === "configured") return "warning";
  if (status === "failed") return "danger";
  return "neutral";
}

function ServiceStatusBadge({ status }: { status: ServiceConfigCard["status"] }) {
  return <span className={`status ${serviceStatusTone(status)}`}><i />{serviceStatusText(status)}</span>;
}

function checkTone(status: ServiceCheckStatus | undefined) {
  if (status === "verified") return "success";
  if (status === "failed") return "danger";
  return "warning";
}

function hasSavedSecret(item: ServiceSubItem | null | undefined) {
  return Boolean(item?.secretSummary && !/未保存|缺少|未配置|待保存/.test(item.secretSummary));
}

function maskedFingerprint(value: string | null | undefined) {
  if (!value) return "安全校验尾号待服务端返回";
  return `安全校验尾号 ${value.slice(-6)}`;
}

function safeVersion(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return "版本待服务端返回";
  return `当前第 ${value} 版`;
}

function serviceNextStep(item: ServiceSubItem) {
  if (item.status === "verified") return "配置已可用，按需复核";
  if (!hasSavedSecret(item)) return "先补齐必填资料";
  if (item.key === "web-wechat-runtime") return "启用后到登录页扫码复核";
  if (item.key === "sms-runtime") return item.status === "failed" ? "检查真实发送结果" : "完成真实发送测试并启用";
  if (item.status === "failed") return "查看阻断原因并重新验证";
  if (item.callbackStatus === "failed") return "修复回调后重新验证";
  if (item.callbackStatus !== "verified" && (item.callbackLabel || item.callbackUrl)) return "完成回调验证";
  return "运行真实测试并申请启用";
}

function enableServiceForCard(card: ServiceConfigCard, item: ServiceSubItem | null): ServiceConfigEnableInput | null {
  if (!item) return null;
  if (card.section === "platform") {
    return { service: "platform_application", provider: item.key, model: null, reason: "", idempotencyKey: "" };
  }
  if (card.section === "ai") {
    const provider = aiProviderDefinitions.find((definition) => item.key === definition.provider || item.key.startsWith(`${definition.provider}-`))?.provider;
    if (!provider) return null;
    const service = card.catalogServices?.find((catalogService) => catalogService.key === provider);
    const activeModel = service?.models.find((model) => model.status === "active") ?? service?.models[0];
    if (!activeModel) return null;
    return { service: "ai", provider, model: activeModel.key, reason: "", idempotencyKey: "" };
  }
  if (item.key === "web-wechat-runtime") {
    return { service: "web_wechat_login", provider: "web-wechat-login", model: null, reason: "", idempotencyKey: "" };
  }
  return { service: "sms", provider: "aliyun", model: null, reason: "", idempotencyKey: "" };
}

function selectedEnableModel(card: ServiceConfigCard, draft: ServiceConfigEnableInput | null) {
  if (!draft || draft.service !== "ai" || !draft.model) return null;
  return card.catalogServices?.find((service) => service.key === draft.provider)?.models.find((model) => model.key === draft.model) ?? null;
}

function serviceEnableReadiness(card: ServiceConfigCard, item: ServiceSubItem | null, draft: ServiceConfigEnableInput | null) {
  if (!draft || !item) return { ready: false, reason: "当前服务缺少可启用对象，请等待服务端返回完整目录。" };
  if (!hasSavedSecret(item)) return { ready: false, reason: "当前服务凭据还未保存，不能执行启用。" };
  if (draft.service === "platform_application") {
    if (item.connectionStatus !== "healthy") return { ready: false, reason: "当前平台接口尚未通过真实连接检查，不能启用。" };
    return { ready: true, reason: "应用资料与真实连接均已确认，可以启用当前平台。" };
  }
  if (draft.service === "ai") {
    const model = selectedEnableModel(card, draft);
    if (!model || model.status !== "active") return { ready: false, reason: "当前模型不在服务端可用目录中，不能启用。" };
    if (item.callbackStatus !== "verified") return { ready: false, reason: "AI 服务必须先通过当前运行配置的真实连通测试。" };
  }
  if (draft.service === "sms") {
    const sendEvidence = card.evidence.find((evidence) => evidence.label.includes("真实发送"));
    if (!sendEvidence || sendEvidence.status !== "verified") return { ready: false, reason: "短信必须先通过真实发送测试，不能仅凭已保存配置启用。" };
    return { ready: true, reason: "短信资料与真实发送测试均已确认，可以启用短信服务。" };
  }
  if (draft.service === "web_wechat_login") {
    return { ready: true, reason: "微信应用编号和密钥已保存，可以先启用登录通道；首次真实扫码用于确认登录结果。" };
  }
  if ((item.callbackLabel || item.callbackUrl) && item.callbackStatus !== "verified") {
    return { ready: false, reason: "当前服务授权尚未通过系统验证，不能进入启用。" };
  }
  return { ready: true, reason: "前置证据已满足，将提交服务端启用校验。" };
}

function PlatformConfigForm({ definition, metadata, centerItem, canManage, canRotate, onSaved }: {
  definition: PlatformDefinition;
  metadata: PlatformApplicationConfigMetadata | undefined;
  centerItem: ServiceSubItem | undefined;
  canManage: boolean;
  canRotate: boolean;
  onSaved: () => Promise<void>;
}) {
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const callbackUrl = metadata?.callbackUrl ?? centerItem?.callbackUrl ?? "";
  const messageUrl = metadata?.messageUrl ?? centerItem?.messageUrl ?? "";
  const hasRequiredCallback = Boolean(callbackUrl);

  async function savePlatformConfig(submittedSecrets: Record<string, string>) {
    await api(`/api/platform-app-configs/${definition.provider}`, {
      method: "PUT",
      body: JSON.stringify({ secrets: submittedSecrets, reason: reason.trim() }),
    });
    setSecrets({});
    setReason("");
    setSaved(true);
    await onSaved();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canManage || !canRotate || !hasRequiredCallback) return;
    setError("");
    setSaved(false);
    const submittedSecrets = Object.fromEntries(Object.entries(secrets)
      .map(([key, value]) => [key, value.trim()] as const)
      .filter(([, value]) => value.length > 0));
    if (reason.trim().length < 8) {
      setError("请填写至少 8 个字的操作原因，系统会写入审计记录。");
      return;
    }
    if (metadata && Object.keys(submittedSecrets).length === 0) {
      setError("请填写需要更新的字段；不填写时系统会保持当前密钥。");
      return;
    }
    setBusy(true);
    try {
      await savePlatformConfig(submittedSecrets);
    } catch (failure) {
      setError(saveErrorMessage(failure, "平台配置保存失败"));
    } finally {
      setBusy(false);
    }
  }

  return <form className="panel platform-config-card service-dedicated-form" onSubmit={submit}>
    <div className="service-platform-config-scroll">
    <header>
      <div className={`platform-config-icon${definition.secondaryLogo ? " multi-logo" : ""}`}><img src={definition.logo} alt={`${definition.title} Logo`} />{definition.secondaryLogo && <img src={definition.secondaryLogo} alt="天猫 Logo" />}</div>
      <div className="platform-config-identity"><h2>{definition.title}</h2><p>{definition.description}</p><div className="service-safe-meta">
        <span><ShieldCheck size={14} aria-hidden />{statusLine(metadata, metadata?.configuredFields.length)}</span>
        {centerItem?.connectionStatus === "healthy" && <span><CheckCircle2 size={14} aria-hidden />接口已连通{centerItem.connectionLastSuccessAt ? ` · ${formatDateTime(centerItem.connectionLastSuccessAt)}` : ""}</span>}
        <span><Webhook size={14} aria-hidden />授权回调：{centerItem?.callbackStatus === "verified" ? "已确认" : centerItem?.connectionStatus === "healthy" ? "待下次授权触发" : checkStatusText(centerItem?.callbackStatus)}</span>
        {metadata && <span><ShieldCheck size={14} aria-hidden />{safeVersion(metadata.activeVersion)} · {maskedFingerprint(metadata.fingerprint)}</span>}
      </div></div>
      <StatusBadge value={metadata ? "configured" : "not_configured"} />
    </header>
    <div className="platform-config-workspace">
      <section className="platform-config-stage platform-config-onboarding">
        <div className="platform-config-stage-title"><span>1</span><div><h3>官方平台准备</h3><p>复制系统地址到官方后台，并确认应用已具备接口权限。</p></div></div>
        <div className="platform-config-readiness"><ul>{definition.operatorSteps.map((step) => <li key={step}>{step}</li>)}</ul><a href={definition.officialUrl} target="_blank" rel="noreferrer">前往{definition.title} <ExternalLink size={13} /></a></div>
        {hasRequiredCallback
          ? <CopyableReadonlyField label={definition.callbackLabel} value={callbackUrl} disabled={!canManage} />
          : <InlineFeedback tone="warning">尚未配置控制面公网访问地址，暂不能生成授权回调地址。请联系平台技术人员完成生产域名配置。</InlineFeedback>}
        {definition.provider === "wechat_shop" && <CopyableReadonlyField label="消息推送地址" value={messageUrl} disabled={!canManage || !messageUrl} />}
        {definition.provider === "wechat_shop" && !messageUrl && <InlineFeedback tone="danger">服务端尚未返回完整消息推送地址，微信小店消息校验不能显示为可用。</InlineFeedback>}
      </section>
      <section className="platform-config-stage platform-config-credentials">
        <div className="platform-config-stage-title"><span>2</span><div><h3>填写应用凭据</h3><p>从官方后台复制完整资料，保存后密钥不会再次显示。</p></div></div>
        <div className="secret-field-grid">{definition.fields.map((field) => {
          const configured = metadata?.configuredFields.includes(field.key) ?? false;
          return <label key={field.key}><span>{field.label}{field.optional ? "（可选）" : ""}{configured && <span className="field-label-actions"><SecretSavedHelp title={field.label} mode="partial" /><i><CheckCircle2 />已配置</i></span>}</span><input type="password" value={secrets[field.key] ?? ""} onChange={(event) => setSecrets({ ...secrets, [field.key]: event.target.value })} placeholder={configured ? "已保存，不回显；留空保持当前值" : field.placeholder} autoComplete="new-password" required={!configured && !field.optional} disabled={!canManage || !canRotate} /></label>;
        })}</div>
      </section>
    </div>
    {error && <div className="form-error">{error}</div>}
    {saved && <div className="form-success">配置已加密保存，等待系统回调或连接探测后再显示已验证。</div>}
    <label className="platform-config-reason">操作原因（至少 8 个字，会写入审计）<textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明本次配置或密钥变更的用途" disabled={!canManage || !canRotate} /></label>
    <div className="platform-config-verify-row">
      <div><strong>3. 当前连接</strong><small>连接状态来自平台真实接口探测，无需人工验证回调。</small></div>
      <div className={`platform-connection-summary ${centerItem?.connectionStatus === "healthy" ? "success" : "pending"}`}>
        {centerItem?.connectionStatus === "healthy" ? <CheckCircle2 aria-hidden /> : <Webhook aria-hidden />}
        <div>
          <strong>{centerItem?.connectionStatus === "healthy" ? "平台连接正常" : "尚未完成真实连接"}</strong>
          <span>{centerItem?.connectionStatus === "healthy"
            ? `最近成功连接${centerItem.connectionLastSuccessAt ? `：${formatDateTime(centerItem.connectionLastSuccessAt)}` : ""}。授权回调由系统在商家重新授权时自动记录。`
            : "保存应用资料并完成商家授权后，系统会通过真实接口探测自动更新状态。"}</span>
        </div>
      </div>
    </div>
    </div>
    <footer>
      <small>{canManage && canRotate ? "有权管理员填写原因后直接保存；留空不会覆盖已保存密钥。" : "当前账号不能修改平台应用配置。"}</small>
      <button className="button primary" disabled={!canManage || !canRotate || !hasRequiredCallback || reason.trim().length < 8 || busy}><Save size={15} />{busy ? "正在安全保存…" : "保存平台配置"}</button>
    </footer>
  </form>;
}

function AiProviderForm({ definition, catalogService, credential, centerItem, routingCredential, selected, canManage, canRotate, canTestService, onSaved, requestApproval, initialApiKey = "", initialBaseUrl = "" }: {
  definition: AiProviderDefinition;
  catalogService: AiCatalogService | undefined;
  credential: CredentialMetadata | undefined;
  centerItem?: ServiceSubItem | undefined;
  routingCredential?: CredentialMetadata | undefined;
  selected: boolean;
  canManage: boolean;
  canRotate: boolean;
  canTestService: boolean;
  onSaved: () => Promise<void>;
  requestApproval: RequestApproval;
  initialApiKey?: string;
  initialBaseUrl?: string;
}) {
  const modelOptions = useMemo(() => (catalogService?.models ?? []).filter((model) => model.name).map((model) => ({ value: model.key, label: `${model.name} · ${model.priceLabel}` })), [catalogService]);
  const [apiKey, setApiKey] = useState(initialApiKey);
  const [baseUrl, setBaseUrl] = useState(initialBaseUrl || (credential ? "" : definition.recommendedBaseUrl));
  const [model, setModel] = useState(modelOptions[0]?.value ?? "");
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<ServiceActionResult | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const selectedModel = catalogService?.models.find((item) => item.key === model);
  const catalogReady = Boolean(catalogService && modelOptions.length);
  const activeCredential = credential?.status === "active";

  useEffect(() => {
    if (!model && modelOptions[0]?.value) setModel(modelOptions[0].value);
  }, [model, modelOptions]);

  useEffect(() => {
    if (!credential && !baseUrl.trim()) setBaseUrl(definition.recommendedBaseUrl);
  }, [baseUrl, credential, definition.recommendedBaseUrl]);

  async function saveAiProvider(secret: string, approvalId?: string, reason?: string) {
    if (credential) {
      await api(`/api/credentials/${credential.id}/rotate`, { method: "POST", body: JSON.stringify({ secret, reason: reason || `更新 ${definition.title} 平台运行配置`, expiresAt: null, ...(approvalId ? { approvalId } : {}) }) });
    } else {
      await api("/api/credentials", { method: "POST", body: JSON.stringify({ scope: "platform", tenantId: null, provider: definition.provider, name: "ai-provider-runtime", secret, expiresAt: null }) });
    }
    setApiKey("");
    setBaseUrl("");
    setSaved(true);
    await onSaved();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canManage || !canRotate || !catalogReady || !model) return;
    if (!apiKey.trim() || !baseUrl.trim()) {
      setError(credential ? "轮换配置需要重新填写完整模型账号密钥和接口地址。" : "请填写模型账号密钥和接口地址。");
      return;
    }
    setError("");
    setSaved(false);
    const secret = JSON.stringify({ apiKey: apiKey.trim(), baseUrl: baseUrl.trim(), model });
    if (credential) {
      const approvalPayload = await credentialRotateApprovalPayload({
        credentialId: credential.id,
        scope: credential.scope,
        tenantId: credential.tenantId ?? null,
        provider: credential.provider,
        submittedSecretFields: ["secret"],
        secretDigestInput: { secret },
      });
      requestApproval({
        operation: { action: "credential.rotate", targetType: "credential", targetId: credential.id, tenantId: credential.tenantId ?? null, payload: approvalPayload },
        title: `轮换 ${definition.title} 模型配置`,
        targetLabel: definition.title,
        impact: ["将替换该模型服务商的账号密钥、接口地址和所选模型。", "审批通过前不会写入新配置，当前 AI 能力继续使用旧配置。"],
        recovery: "如新模型配置异常，可再次发起审批轮换回可用配置；审计记录会保留。",
        execute: async (approvalId, reason) => {
          setBusy(true);
          try {
            await saveAiProvider(secret, approvalId, reason);
          } catch (failure) {
            const message = saveErrorMessage(failure, "AI 模型服务配置保存失败");
            setError(message);
            throw new Error(message);
          } finally {
            setBusy(false);
          }
        },
      });
      return;
    }
    setBusy(true);
    try {
      await saveAiProvider(secret);
    } catch (failure) {
      setError(saveErrorMessage(failure, "AI 模型服务配置保存失败"));
    } finally {
      setBusy(false);
    }
  }

  async function saveDefaultRoute(approvalId?: string) {
    await api("/api/ai-routing/default", { method: "PUT", body: JSON.stringify({ provider: definition.provider, ...(approvalId ? { approvalId } : {}) }) });
    setSaved(true);
    await onSaved();
  }

  async function setDefault() {
    if (!canManage || !credential || selected) return;
    setError("");
    setSaved(false);
    if (routingCredential) {
      const routingPayload = JSON.stringify({
        selectedProviderId: definition.provider,
        routes: [{ agentId: "master_agent", enabled: true, primaryProviderId: definition.provider }],
      });
      const approvalPayload = await credentialRotateApprovalPayload({
        credentialId: routingCredential.id,
        scope: routingCredential.scope,
        tenantId: routingCredential.tenantId ?? null,
        provider: routingCredential.provider,
        submittedSecretFields: ["routingPayload"],
        secretDigestInput: { routingPayload },
      });
      requestApproval({
        operation: { action: "credential.rotate", targetType: "credential", targetId: routingCredential.id, tenantId: routingCredential.tenantId ?? null, payload: approvalPayload },
        title: `切换 AI 默认路线到 ${definition.title}`,
        targetLabel: "AI 默认路线",
        impact: ["平台助手会默认使用新的模型服务商，费用、速度和错误类型可能变化。", "审批通过前默认路线不会改变。"],
        recovery: "如切换后不符合预期，可再次发起审批切回其他已配置模型服务商。",
        execute: async (approvalId) => {
          setSwitching(true);
          try {
            await saveDefaultRoute(approvalId);
          } catch (failure) {
            const message = saveErrorMessage(failure, "AI 默认服务商切换失败");
            setError(message);
            throw new Error(message);
          } finally {
            setSwitching(false);
          }
        },
      });
      return;
    }
    setSwitching(true);
    try {
      await saveDefaultRoute();
    } catch (failure) {
      setError(saveErrorMessage(failure, "AI 默认服务商切换失败"));
    } finally {
      setSwitching(false);
    }
  }

  async function runConnectivityTest() {
    if (!canTestService) return;
    if (!catalogReady || !model) {
      setTestResult({ status: "failed", title: "连通测试未完成", detail: "请先从服务端目录选择模型。", checkedAt: null });
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    try {
      const result = await api<RawVerificationResponse>("/api/service-config/ai/connectivity-test", {
        method: "POST",
        body: JSON.stringify({ provider: definition.provider, model, prompt: "请回复 OK，用于控制面连通测试。", idempotencyKey: makeActionKey("ai-connectivity-test") }),
      });
      setTestResult(actionResult(result, "AI 连通测试结果"));
      await onSaved();
    } catch (failure) {
      setTestResult(failedActionResult(failure, "AI 连通测试失败，当前不会显示为已验证。"));
    } finally {
      setTestBusy(false);
    }
  }

  return <form className="panel platform-config-card service-dedicated-form service-ai-form" onSubmit={submit}>
    <header><div className="platform-config-icon"><Bot aria-hidden="true" /></div><div><h2>{definition.title}</h2><p>{definition.vendor}</p></div><div className="provider-card-status">{selected && <span className="role-chip">当前默认</span>}<StatusBadge value={credential?.status ?? "not_configured"} /></div></header>
    <div className="service-safe-meta">
      <span><ShieldCheck size={14} aria-hidden />{statusLine(credential)}</span>
      <span><TestTube2 size={14} aria-hidden />连通测试：{checkStatusText(centerItem?.callbackStatus)}</span>
      {credential && <span><ShieldCheck size={14} aria-hidden />{safeVersion(credential.activeVersion)} · {maskedFingerprint(credential.fingerprint)}</span>}
    </div>
    {!catalogReady && <InlineFeedback tone="danger">服务端模型与价格目录未接入，不能保存或切换为可用模型。</InlineFeedback>}
    <label><span className="field-label-row">模型账号密钥{credential && !apiKey && <SecretSavedHelp title="模型账号密钥" mode="full" />}</span><input type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} minLength={8} autoComplete="new-password" placeholder={credential ? "已保存，不回显；轮换时请重新填写完整密钥" : "输入服务商提供的模型账号密钥"} required={catalogReady} disabled={!canManage || !canRotate || !catalogReady} /></label>
    <details className="service-inline-advanced">
      <summary>高级：模型接口地址</summary>
      <label><span className="field-label-row">模型接口地址</span><input type="url" value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} placeholder={definition.baseUrlPlaceholder} required={catalogReady} disabled={!canManage || !canRotate || !catalogReady} /><small className="field-hint">首次配置默认使用服务商推荐地址；如企业有专属地址可在这里调整。</small></label>
    </details>
    <label className="full">模型与价格目录{modelOptions.length
      ? <V3Select value={model} onChange={setModel} ariaLabel={`${definition.title} 模型与价格目录`} disabled={!canManage || !canRotate || !catalogReady} options={modelOptions} />
      : <span className="form-hint">等待服务端返回模型、价格、预算和降级口径。</span>}</label>
    {selectedModel && <div className="service-model-evidence">
      <span><strong>价格</strong><small>{selectedModel.priceLabel}</small></span>
      <span><strong>适用场景</strong><small>{selectedModel.purpose}</small></span>
      <span><strong>预算</strong><small>{selectedModel.budgetLabel ?? "预算待服务端确认"}</small></span>
      <span><strong>降级</strong><small>{selectedModel.fallbackLabel ?? "降级策略待服务端确认"}</small></span>
    </div>}
    <div className="service-test-actions">
      <button type="button" className="button secondary" disabled={!canTestService || !catalogReady || !activeCredential || testBusy} onClick={() => { void runConnectivityTest(); }}>{testBusy ? "测试中…" : "真实连通测试"}</button>
      <ActionResultView result={testResult} empty="测试结果只按服务端真实连通返回展示。" />
    </div>
    {error && <div className="form-error">{error}</div>}
    {saved && <div className="form-success">操作已写入加密保险库，等待真实连通测试和审计证据。</div>}
    <footer><small>{selected ? "AI 平台助手默认使用这一家；切换结果以服务端保存为准。" : activeCredential ? "已保存配置；轮换需要完整重填密钥和接口地址。" : "先从服务端目录选择模型，再保存账号配置。"}</small><div className="provider-card-actions">{credential && <button type="button" className="button secondary" disabled={!canManage || !activeCredential || selected || switching} onClick={() => { void setDefault(); }}>{switching ? "正在切换…" : selected ? "当前默认" : "设为默认"}</button>}<button className="button primary" disabled={!canManage || !canRotate || !catalogReady || busy}><Save size={15} />{busy ? "正在保存…" : credential ? "轮换配置" : "保存配置"}</button></div></footer>
  </form>;
}

function SmsForm({ credential, callbackUrl, callbackStatus, canManage, canRotate, canVerifyCallback, canTestService, onSaved, requestApproval }: {
  credential: CredentialMetadata | undefined;
  callbackUrl: string;
  callbackStatus?: ServiceCheckStatus | undefined;
  canManage: boolean;
  canRotate: boolean;
  canVerifyCallback: boolean;
  canTestService: boolean;
  onSaved: () => Promise<void>;
  requestApproval: RequestApproval;
}) {
  const [mode, setMode] = useState(smsDefaults.provider);
  const [accessKeyId, setAccessKeyId] = useState("");
  const [accessKeySecret, setAccessKeySecret] = useState("");
  const [regionId, setRegionId] = useState(smsDefaults.regionId);
  const [endpoint, setEndpoint] = useState(smsDefaults.endpoint);
  const [signName, setSignName] = useState("");
  const [templateCode, setTemplateCode] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [callbackBusy, setCallbackBusy] = useState(false);
  const [testResult, setTestResult] = useState<ServiceActionResult | null>(null);
  const [callbackResult, setCallbackResult] = useState<ServiceActionResult | null>(null);
  const [rotationMode, setRotationMode] = useState(!credential);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const active = credential?.status === "active";
  const hasCallback = Boolean(callbackUrl);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canManage || !canRotate || !hasCallback || (credential && !rotationMode)) return;
    if (mode !== "dry-run" && (!accessKeyId.trim() || !accessKeySecret.trim() || !signName.trim() || !templateCode.trim())) {
      setError(credential ? "轮换短信配置需要重新填写完整账号、签名和模板。" : "请填写短信账号、签名和模板。");
      return;
    }
    setError("");
    setSaved(false);
    const secret = JSON.stringify({
      provider: mode === "dry-run" ? "dry-run" : "aliyun",
      accessKeyId: accessKeyId.trim(),
      accessKeySecret: accessKeySecret.trim(),
      regionId: regionId.trim() || smsDefaults.regionId,
      signName: signName.trim(),
      templateCode: templateCode.trim(),
      endpoint: endpoint.trim() || smsDefaults.endpoint,
      callbackUrl,
    });
    async function saveSmsConfig(approvalId?: string, reason?: string) {
      if (credential) {
        await api(`/api/credentials/${credential.id}/rotate`, { method: "POST", body: JSON.stringify({ secret, reason: reason || "更新短信网关运行配置", expiresAt: null, ...(approvalId ? { approvalId } : {}) }) });
      } else {
        await api("/api/credentials", { method: "POST", body: JSON.stringify({ scope: "platform", tenantId: null, provider: "sms", name: "sms-runtime", secret, expiresAt: null }) });
      }
      setAccessKeyId("");
      setAccessKeySecret("");
      setSignName("");
      setTemplateCode("");
      setSaved(true);
      await onSaved();
    }
    if (credential) {
      const approvalPayload = await credentialRotateApprovalPayload({
        credentialId: credential.id,
        scope: credential.scope,
        tenantId: credential.tenantId ?? null,
        provider: credential.provider,
        submittedSecretFields: ["secret"],
        secretDigestInput: { secret },
      });
      requestApproval({
        operation: { action: "credential.rotate", targetType: "credential", targetId: credential.id, tenantId: credential.tenantId ?? null, payload: approvalPayload },
        title: "轮换验证码短信配置",
        targetLabel: "验证码短信",
        impact: ["将替换短信账号、签名、模板和发送通道配置。", "审批通过前不会写入新配置，短信服务继续使用旧配置。"],
        recovery: "如真实发送测试失败，可再次发起审批写入正确配置。",
        execute: async (approvalId, reason) => {
          setBusy(true);
          try {
            await saveSmsConfig(approvalId, reason);
          } catch (failure) {
            const message = saveErrorMessage(failure, "短信服务配置保存失败");
            setError(message);
            throw new Error(message);
          } finally {
            setBusy(false);
          }
        },
      });
      return;
    }
    setBusy(true);
    try {
      await saveSmsConfig();
    } catch (failure) {
      setError(saveErrorMessage(failure, "短信服务配置保存失败"));
    } finally {
      setBusy(false);
    }
  }

  async function runSendTest() {
    if (!canTestService) return;
    if (!testPhone.trim()) {
      setTestResult({ status: "failed", title: "真实发送测试未完成", detail: "请先填写测试手机号。", checkedAt: null });
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    try {
      const result = await api<RawVerificationResponse>("/api/service-config/sms/send-test", {
        method: "POST",
        body: JSON.stringify({ phone: testPhone.trim(), templateParams: { code: "123456" }, idempotencyKey: makeActionKey("sms-send-test") }),
      });
      setTestResult(actionResult(result, "短信真实发送测试结果"));
      await onSaved();
    } catch (failure) {
      setTestResult(failedActionResult(failure, "短信真实发送测试失败，当前不会显示为已验证。"));
    } finally {
      setTestBusy(false);
    }
  }

  async function verifySmsCallback() {
    if (!canVerifyCallback) return;
    setCallbackBusy(true);
    setCallbackResult(null);
    try {
      const result = await api<RawVerificationResponse>("/api/service-config/callbacks/verify", {
        method: "POST",
        body: JSON.stringify({ provider: "aliyun", subject: "sms_callback", idempotencyKey: makeActionKey("sms-callback-verify") }),
      });
      setCallbackResult(actionResult(result, "短信回调验证结果"));
      await onSaved();
    } catch (failure) {
      setCallbackResult(failedActionResult(failure, "短信回调验证失败，当前不会显示为已验证。"));
    } finally {
      setCallbackBusy(false);
    }
  }

  return <form className="panel platform-config-card service-dedicated-form service-sms-form" onSubmit={submit}>
    <header><div className="platform-config-icon"><MessageSquareText aria-hidden="true" /></div><div><h2>验证码短信</h2><p>登录验证码 / 注册校验 / 密码重置的短信发送通道</p></div><StatusBadge value={credential ? "configured" : "not_configured"} /></header>
    <div className="service-safe-meta">
      <span><ShieldCheck size={14} aria-hidden />{statusLine(credential)}</span>
      <span><TestTube2 size={14} aria-hidden />真实发送测试：未验证</span>
      <span><Webhook size={14} aria-hidden />送达回执（可选）：{checkStatusText(callbackStatus)}</span>
      {credential && <span><ShieldCheck size={14} aria-hidden />{safeVersion(credential.activeVersion)} · {maskedFingerprint(credential.fingerprint)}</span>}
    </div>
    <CopyableReadonlyField label="短信回执地址" value={callbackUrl} disabled={!canManage || !callbackUrl} />
    {!hasCallback && <InlineFeedback tone="danger">服务端尚未返回短信回执地址，不能把短信配置保存为可用服务。</InlineFeedback>}
    {credential && !rotationMode
      ? <InlineFeedback tone="info">短信账号、签名和模板已安全保存。真实发送测试会直接使用当前配置，无需再次填写。</InlineFeedback>
      : <label className="full"><span className="field-label-row">运行模式</span><V3Select value={mode} onChange={setMode} ariaLabel="运行模式" disabled={!canManage || !canRotate || !hasCallback} options={[
        { value: "aliyun", label: "阿里云真实发送", hint: "验证码通过阿里云实际下发" },
        { value: "dry-run", label: "测试模式（不实际发送）", hint: "联调验证用，不产生短信费用" },
      ]} /></label>}
    <div className="secret-field-grid">
      {(!credential || rotationMode) && <>
        <label><span className="secret-label-row">短信账号编号{credential && !accessKeyId && <SecretSavedHelp title="短信账号编号" mode="full" />}</span><input type="password" value={accessKeyId} onChange={(event) => setAccessKeyId(event.target.value)} minLength={8} autoComplete="new-password" placeholder="从服务商控制台复制账号编号" required={mode !== "dry-run"} disabled={!canManage || !canRotate || !hasCallback} /></label>
        <label><span className="secret-label-row">短信账号密钥{credential && !accessKeySecret && <SecretSavedHelp title="短信账号密钥" mode="full" />}</span><input type="password" value={accessKeySecret} onChange={(event) => setAccessKeySecret(event.target.value)} minLength={8} autoComplete="new-password" placeholder="从服务商控制台复制账号密钥" required={mode !== "dry-run"} disabled={!canManage || !canRotate || !hasCallback} /></label>
        <label>短信签名<input value={signName} onChange={(event) => setSignName(event.target.value)} placeholder="服务商控制台已报备的签名" required={mode !== "dry-run"} disabled={!canManage || !canRotate || !hasCallback} /></label>
        <label>短信模板编号<input value={templateCode} onChange={(event) => setTemplateCode(event.target.value)} placeholder="服务商控制台审核通过的模板编号" required={mode !== "dry-run"} disabled={!canManage || !canRotate || !hasCallback} /></label>
        <details className="service-inline-advanced">
        <summary>高级：服务区域与接口地址</summary>
        <label>服务区域<input value={regionId} onChange={(event) => setRegionId(event.target.value)} placeholder="默认 cn-hangzhou" disabled={mode === "dry-run" || !canManage || !canRotate || !hasCallback} /></label>
        <label>短信接口地址<input value={endpoint} onChange={(event) => setEndpoint(event.target.value)} placeholder="默认 dysmsapi.aliyuncs.com" disabled={mode === "dry-run" || !canManage || !canRotate || !hasCallback} /></label>
        </details>
      </>}
      <label>测试手机号<input type="tel" value={testPhone} onChange={(event) => setTestPhone(event.target.value)} placeholder="用于真实发送测试，不保存为密钥" disabled={!canManage || !canRotate || !active} /></label>
    </div>
    <div className="service-test-actions">
      <button type="button" className="button secondary" disabled={!canTestService || !active || !testPhone.trim() || testBusy} onClick={() => { void runSendTest(); }}>{testBusy ? "发送中…" : "真实发送测试"}</button>
      <button type="button" className="button secondary" disabled={!canVerifyCallback || !active || !hasCallback || callbackBusy} onClick={() => { void verifySmsCallback(); }}>{callbackBusy ? "验证中…" : "验证短信回执"}</button>
      <ActionResultView result={testResult} empty="真实发送测试会展示服务端回执与余额状态。" />
      <ActionResultView result={callbackResult} empty="回调验证只按服务端真实结果展示。" />
    </div>
    {error && <div className="form-error">{error}</div>}
    {saved && <div className="form-success">配置已加密保存，完成真实发送测试后即可启用；送达回执会在服务商返回后自动记录。</div>}
    <footer><small>{active ? "测试直接使用已保存配置；仅更换账号、签名或模板时才需要重新填写。" : "请先保存短信账号、签名与模板，再执行真实发送测试。"}</small>{credential && !rotationMode
      ? <button type="button" className="button secondary" disabled={!canManage || !canRotate} onClick={() => setRotationMode(true)}>更换账号或模板</button>
      : <span className="provider-card-actions">{credential && <button type="button" className="button secondary" onClick={() => setRotationMode(false)}>取消更换</button>}<button className="button primary" disabled={!canManage || !canRotate || !hasCallback || busy}><Save size={15} />{busy ? "正在保存…" : credential ? "提交更换审批" : "保存配置"}</button></span>}</footer>
  </form>;
}

function WebWechatLoginForm({ credential, callbackUrl, callbackStatus, canManage, canRotate, onSaved, requestApproval }: {
  credential: CredentialMetadata | undefined;
  callbackUrl: string;
  callbackStatus?: ServiceCheckStatus | undefined;
  canManage: boolean;
  canRotate: boolean;
  onSaved: () => Promise<void>;
  requestApproval: RequestApproval;
}) {
  const [enabled, setEnabled] = useState(true);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const hasCallback = Boolean(callbackUrl);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canManage || !canRotate || !hasCallback) return;
    if (enabled && (!appId.trim() || !appSecret.trim())) {
      setError(credential ? "轮换微信扫码登录需要重新填写完整应用编号和应用密钥。" : "请填写微信应用编号和应用密钥。");
      return;
    }
    setError("");
    setSaved(false);
    const secret = JSON.stringify({ enabled, appId: appId.trim(), appSecret: appSecret.trim(), redirectUri: callbackUrl });
    async function saveWebWechatConfig(approvalId?: string, reason?: string) {
      if (credential) {
        await api(`/api/credentials/${credential.id}/rotate`, { method: "POST", body: JSON.stringify({ secret, reason: reason || "更新微信扫码登录运行配置", expiresAt: null, ...(approvalId ? { approvalId } : {}) }) });
      } else {
        await api("/api/credentials", { method: "POST", body: JSON.stringify({ scope: "platform", tenantId: null, provider: "web-wechat-login", name: "web-wechat-runtime", secret, expiresAt: null }) });
      }
      setAppId("");
      setAppSecret("");
      setSaved(true);
      await onSaved();
    }
    if (credential) {
      const approvalPayload = await credentialRotateApprovalPayload({
        credentialId: credential.id,
        scope: credential.scope,
        tenantId: credential.tenantId ?? null,
        provider: credential.provider,
        submittedSecretFields: ["secret"],
        secretDigestInput: { secret },
      });
      requestApproval({
        operation: { action: "credential.rotate", targetType: "credential", targetId: credential.id, tenantId: credential.tenantId ?? null, payload: approvalPayload },
        title: "轮换微信扫码登录配置",
        targetLabel: "微信扫码登录",
        impact: ["将替换微信网站应用编号、密钥和授权回调配置。", "审批通过前不会写入新配置，当前登录能力继续使用旧配置。"],
        recovery: "如扫码登录异常，可再次发起审批写入正确配置。",
        execute: async (approvalId, reason) => {
          setBusy(true);
          try {
            await saveWebWechatConfig(approvalId, reason);
          } catch (failure) {
            const message = saveErrorMessage(failure, "微信扫码登录配置保存失败");
            setError(message);
            throw new Error(message);
          } finally {
            setBusy(false);
          }
        },
      });
      return;
    }
    setBusy(true);
    try {
      await saveWebWechatConfig();
    } catch (failure) {
      setError(saveErrorMessage(failure, "微信扫码登录配置保存失败"));
    } finally {
      setBusy(false);
    }
  }

  return <form className="panel platform-config-card service-dedicated-form service-sms-form service-wechat-form" onSubmit={submit}>
    <header><div className="platform-config-icon"><QrCode aria-hidden="true" /></div><div><h2>微信扫码登录</h2><p>微信开放平台网站应用的应用编号、应用密钥与授权回调地址</p></div><StatusBadge value={credential ? "configured" : "not_configured"} /></header>
    <div className="service-safe-meta">
      <span><ShieldCheck size={14} aria-hidden />{statusLine(credential)}</span>
      <span><Webhook size={14} aria-hidden />回调状态：{checkStatusText(callbackStatus)}</span>
      {credential && <span><ShieldCheck size={14} aria-hidden />{safeVersion(credential.activeVersion)} · {maskedFingerprint(credential.fingerprint)}</span>}
    </div>
    {!hasCallback && <InlineFeedback tone="danger">服务端尚未返回微信扫码登录回调地址，不能保存为可用配置。</InlineFeedback>}
    <label className="checkbox-line"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} disabled={!canManage || !canRotate || !hasCallback} />启用微信扫码登录</label>
    <div className="secret-field-grid">
      <label><span className="secret-label-row">微信应用编号{credential && !appId && <SecretSavedHelp title="微信应用编号" mode="full" />}</span><input type="password" value={appId} onChange={(event) => setAppId(event.target.value)} minLength={8} autoComplete="new-password" placeholder={credential ? "已保存，不回显；轮换时请重新填写完整编号" : "从微信开放平台复制 AppID"} required={enabled} disabled={!canManage || !canRotate || !hasCallback} /></label>
      <label><span className="secret-label-row">微信应用密钥{credential && !appSecret && <SecretSavedHelp title="微信应用密钥" mode="full" />}</span><input type="password" value={appSecret} onChange={(event) => setAppSecret(event.target.value)} minLength={8} autoComplete="new-password" placeholder={credential ? "已保存，不回显；轮换时请重新填写完整密钥" : "从微信开放平台复制 AppSecret"} required={enabled} disabled={!canManage || !canRotate || !hasCallback} /></label>
      <CopyableReadonlyField label="授权回调地址" value={callbackUrl} disabled={!canManage || !callbackUrl} />
    </div>
    {error && <div className="form-error">{error}</div>}
    {saved && <div className="form-success">微信扫码登录配置已加密保存，等待系统回调验证后再显示已验证。</div>}
    <footer><small>{canManage && canRotate ? "该配置独立于短信服务；密钥不回显，轮换时需重新提交完整参数。" : "当前账号不能修改微信扫码登录配置。"}</small><button className="button primary" disabled={!canManage || !canRotate || !hasCallback || busy}><Save size={15} />{busy ? "正在保存…" : credential ? "轮换配置" : "保存配置"}</button></footer>
  </form>;
}

function ReadonlyServiceBoundary() {
  return <SectionCard icon={ShieldCheck} title="高级配置受保护" description="当前账号只能查看统一中心返回的服务状态、更新时间和验证结论。">
    <InlineFeedback tone="info">底层安全参数和原始配置只对平台负责人或安全管理员在审批流程中可操作，普通运营只看服务可用性、验证状态和更新时间。</InlineFeedback>
  </SectionCard>;
}

function AdvancedSettingsDisclosure({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return <details className="service-advanced-disclosure">
    <summary>
      <span><Settings2 size={16} aria-hidden /><strong>{title}</strong></span>
      <small>{description}</small>
    </summary>
    <div className="service-advanced-disclosure-body">{children}</div>
  </details>;
}

function ServiceEvidenceDialog({ card, item, onConfigure, onClose, children, primaryAction }: { card: ServiceConfigCard; item?: ServiceSubItem; onConfigure?: () => void; onClose: () => void; children?: ReactNode; primaryAction?: ReactNode }) {
  const platformConnectionStatus = item?.connectionStatus === "healthy"
    ? "verified" as const
    : item?.connectionStatus === "degraded" || item?.connectionStatus === "revoked"
      ? "failed" as const
      : "unverified" as const;
  const platformEvidence = item ? [
    { label: "应用资料", value: hasSavedSecret(item) ? "必填资料已保存" : "尚未填写，不影响其他平台使用", status: hasSavedSecret(item) ? "verified" as const : "unverified" as const },
    { label: "平台连接", value: platformConnectionStatus === "verified" ? "接口已通过真实连接检查" : platformConnectionStatus === "failed" ? (item.connectionLastError || "平台连接异常，请重新检查") : "保存资料后执行真实连接检查", status: platformConnectionStatus },
    { label: "启用状态", value: item.status === "verified" ? "当前平台已经启用" : platformConnectionStatus === "verified" ? "连接正常，可以启用当前平台" : "连接通过后即可启用", status: item.status === "verified" ? "verified" as const : "unverified" as const },
  ] : [];
  const smsSendEvidence = card.evidence.find((evidence) => evidence.label.includes("真实发送"));
  const serviceCheck = item?.key === "sms-runtime"
    ? { label: "真实发送", value: smsSendEvidence?.status === "verified" ? "最近一次真实短信发送成功" : smsSendEvidence?.status === "failed" ? "最近一次真实发送失败，请检查账号、签名、模板或余额" : "保存资料后向测试手机号发送真实短信", status: smsSendEvidence?.status ?? "unverified" as const }
    : item?.key === "web-wechat-runtime"
      ? { label: "登录准备", value: item.status === "verified" ? "登录通道已启用，可通过登录页扫码复核" : "应用编号和密钥已保存，具备启用条件", status: hasSavedSecret(item) ? "verified" as const : "unverified" as const }
      : { label: item?.callbackLabel ?? (card.section === "ai" ? "模型连通" : "服务验证"), value: item?.callbackDetail || (item?.callbackStatus === "verified" ? "已通过服务端真实验证" : "等待服务端真实验证"), status: item?.callbackStatus ?? "unverified" as const };
  const serviceEvidence = item ? [
    { label: "服务资料", value: hasSavedSecret(item) ? "运行配置已加密保存" : "尚未保存完整运行配置", status: hasSavedSecret(item) ? "verified" as const : "unverified" as const },
    serviceCheck,
    { label: "启用状态", value: item.status === "verified" ? "当前服务已经启用" : item.key === "web-wechat-runtime" && serviceCheck.status === "verified" ? "资料完整，可以启用后扫码复核" : serviceCheck.status === "verified" ? "真实验证通过，可以启用当前服务" : "真实验证通过后即可启用", status: item.status === "verified" ? "verified" as const : "unverified" as const },
  ] : [];
  const evidence = item ? (card.section === "platform" ? platformEvidence : serviceEvidence) : card.evidence;
  const failedCount = evidence.filter((check) => check.status === "failed").length;
  const verifiedCount = evidence.filter((check) => check.status === "verified").length;
  const notStarted = Boolean(item && !hasSavedSecret(item));
  return <V3Dialog
    title={item ? `${item.title}上线前检查` : `${card.title}上线前检查`}
    description={item ? (card.section === "platform" ? "只检查当前平台，不影响其他平台的配置和使用。" : "只检查当前服务，结果来自服务端真实配置与测试。") : "查看当前服务的真实检查结果。"}
    className="service-evidence-dialog"
    wide
    onClose={onClose}
  >
    <div className="service-evidence-dialog-content">
      <div className="service-evidence-dialog-scroll">
        <div className={`service-evidence-summary ${failedCount ? "danger" : verifiedCount === evidence.length && evidence.length ? "success" : notStarted ? "neutral" : "warning"}`}>
          <span className="service-evidence-summary-icon"><ShieldCheck size={20} aria-hidden /></span>
          <div><strong>{failedCount ? `${failedCount} 项需要处理` : verifiedCount === evidence.length && evidence.length ? (card.section === "platform" ? "当前平台已满足上线条件" : "当前服务已满足上线条件") : notStarted ? (card.section === "platform" ? "当前平台尚未配置" : "当前服务尚未配置") : (card.section === "platform" ? "当前平台仍有检查项未完成" : "当前服务仍有检查项未完成")}</strong><p>{notStarted ? (card.section === "platform" ? "需要接入时再填写资料；该状态不会阻塞其他平台。" : "需要使用时再完成配置；不会影响同类其他服务。") : <>已通过 <span className="app-number">{verifiedCount}</span> / <span className="app-number">{evidence.length}</span> 项。</>}</p></div>
        </div>
        <div className="service-evidence-list" role="list" aria-label={`${item?.title ?? card.title}上线前检查`}>
          {evidence.map((check, index) => <article key={check.label} className={`service-evidence-item ${checkTone(check.status)}`} role="listitem">
            <span className="service-evidence-index app-number">{index + 1}</span>
            <div><strong>{check.label}</strong><p>{check.value}</p></div>
            <StatusBadge value={check.status ?? "unverified"} />
          </article>)}
          {evidence.length === 0 && <div className="service-evidence-empty">暂无可检查记录。</div>}
        </div>
        {children}
      </div>
      <footer className="service-evidence-dialog-footer">{onConfigure && <button type="button" className={`button ${primaryAction ? "secondary" : "primary"}`} onClick={onConfigure}>{notStarted ? "开始配置" : "查看配置"}</button>}{primaryAction}<button type="button" className="button secondary" onClick={onClose}>关闭</button></footer>
    </div>
  </V3Dialog>;
}

function ServiceItemEvidenceDialog({ card, item, automaticCheck, canTestService, onConfigure, onClose, onReload, children }: { card: ServiceConfigCard; item: ServiceSubItem; automaticCheck?: { busy: boolean; result: ServiceActionResult | null } | undefined; canTestService: boolean; onConfigure: () => void; onClose: () => void; onReload: () => Promise<void>; children?: ReactNode }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ServiceActionResult | null>(null);
  const [smsTestPhone, setSmsTestPhone] = useState("");
  const [smsTestBusy, setSmsTestBusy] = useState(false);
  const [smsTestResult, setSmsTestResult] = useState<ServiceActionResult | null>(null);
  const enableDraft = enableServiceForCard(card, item);
  const readiness = serviceEnableReadiness(card, item, enableDraft);
  const configured = hasSavedSecret(item);
  const needsSmsTest = item.key === "sms-runtime" && configured;

  async function runSmsPreflightTest() {
    if (!canTestService || !smsTestPhone.trim()) return;
    setSmsTestBusy(true);
    setSmsTestResult(null);
    try {
      const response = await api<RawVerificationResponse>("/api/service-config/sms/send-test", {
        method: "POST",
        body: JSON.stringify({ phone: smsTestPhone.trim(), templateParams: { code: "123456" }, idempotencyKey: makeActionKey("sms-preflight-send-test") }),
      });
      setSmsTestResult(actionResult(response, "短信真实发送测试结果"));
      await onReload();
    } catch (failure) {
      setSmsTestResult(failedActionResult(failure, "短信真实发送测试失败，当前不会显示为已验证。"));
    } finally {
      setSmsTestBusy(false);
    }
  }

  async function enableService() {
    if (!enableDraft || !readiness.ready || reason.trim().length < 8) return;
    setBusy(true);
    setResult(null);
    try {
      await api("/api/service-config/enable", {
        method: "POST",
        body: JSON.stringify({ ...enableDraft, reason: reason.trim(), idempotencyKey: makeActionKey("service-config-enable") }),
      });
      setResult({ status: "verified", title: "服务端启用已完成", detail: "服务端已确认当前配置可用，状态将按最新结果刷新。" });
      await onReload();
    } catch (failure) {
      setResult(failedActionResult(failure, "服务启用失败，当前不会显示为已启用。"));
    } finally {
      setBusy(false);
    }
  }

  return <ServiceEvidenceDialog
    card={card}
    item={item}
    onConfigure={onConfigure}
    onClose={onClose}
    primaryAction={configured ? <button type="button" className="button primary" disabled={!readiness.ready || reason.trim().length < 8 || busy} onClick={() => { void enableService(); }}>{busy ? "正在启用…" : card.section === "platform" ? "启用当前平台" : card.section === "ai" ? "启用当前模型服务" : item.key === "web-wechat-runtime" ? "启用微信扫码登录" : "启用验证码短信"}</button> : undefined}
  >
    {automaticCheck?.busy && <InlineFeedback tone="info">正在使用已保存的配置执行真实连通测试，请稍候…</InlineFeedback>}
    {!automaticCheck?.busy && automaticCheck?.result && <ActionResultView result={automaticCheck.result} empty="" />}
    {needsSmsTest && <div className="service-sms-preflight-test">
      <label>接收测试短信的手机号<input type="tel" value={smsTestPhone} onChange={(event) => setSmsTestPhone(event.target.value)} placeholder="填写本次测试手机号，不会保存" /></label>
      <button type="button" className="button primary" disabled={!canTestService || !smsTestPhone.trim() || smsTestBusy} onClick={() => { void runSmsPreflightTest(); }}>{smsTestBusy ? "正在发送…" : "发送短信并检查"}</button>
      <ActionResultView result={smsTestResult} empty="使用已保存的短信账号、签名和模板发送一条真实验证码短信。" />
    </div>}
    {configured && <div className="service-platform-enable">
      {readiness.ready
        ? <label>启用原因<textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明本次启用依据，至少 8 个字" /></label>
        : <InlineFeedback tone="warning">{readiness.reason}</InlineFeedback>}
      <ActionResultView result={result} empty="尚未执行启用" />
    </div>}
    {children}
  </ServiceEvidenceDialog>;
}

function rollbackWindowSection(window: CredentialRollbackWindow): ServiceConfigSection {
  if (window.name === "ai-provider-runtime" || window.name === "ai-agent-routing" || aiProviderDefinitions.some((item) => item.provider === window.provider)) return "ai";
  if (window.name === "sms-runtime" || window.name === "web-wechat-runtime" || window.provider === "sms" || window.provider === "web-wechat-login") return "sms";
  return "platform";
}

function rollbackWindowLabel(window: CredentialRollbackWindow) {
  const provider = businessLabel(rollbackProviderLabels, window.provider, "服务待确认");
  const name = businessLabel(rollbackCredentialNameLabels, window.name, "配置项");
  return `${provider} · ${name}`;
}

function rollbackImpact(window: CredentialRollbackWindow) {
  if (window.name === "ai-agent-routing") return "平台助手默认模型路线会恢复到目标版本。";
  if (window.name === "ai-provider-runtime") return "该模型服务商的连接密钥、接口地址和模型选择会恢复到目标版本。";
  if (window.name === "sms-runtime") return "验证码短信发送通道会恢复到目标版本，建议随后执行真实发送测试。";
  if (window.name === "web-wechat-runtime") return "微信扫码登录会恢复到目标版本，建议随后复核扫码登录回调。";
  return "该平台应用连接配置会恢复到目标版本，依赖授权或消息回调的能力会随之切换。";
}

function isRollbackWindowOpen(window: CredentialRollbackWindow) {
  return window.status === "open" && new Date(window.expiresAt).getTime() > Date.now();
}

function CredentialRollbackWindowsPanel({ section, windows, unavailable, requestApproval, onReload }: {
  section: ServiceConfigSection;
  windows: CredentialRollbackWindow[];
  unavailable: boolean;
  requestApproval: RequestApproval;
  onReload: () => Promise<void>;
}) {
  const [busyWindowId, setBusyWindowId] = useState<string | null>(null);
  const rows = windows.filter((window) => rollbackWindowSection(window) === section);
  if (rows.length === 0 && !unavailable) return null;
  const columns: Array<DataTableColumn<CredentialRollbackWindow>> = [
    { key: "service", header: "服务", width: "24%", render: (window) => <strong>{rollbackWindowLabel(window)}</strong> },
    { key: "target", header: "目标版本", width: "18%", render: (window) => <span className="app-number">从 v{window.fromVersion} 回到 v{window.toVersion}</span> },
    { key: "expires", header: "窗口到期", width: "18%", render: (window) => <span className="app-number">{formatDateTime(window.expiresAt)}</span> },
    { key: "impact", header: "影响", render: (window) => <span>{rollbackImpact(window)}</span> },
    { key: "reason", header: "原因", render: (window) => <span>{window.reason || "等待服务端返回原因"}</span> },
  ];

  function requestRollback(window: CredentialRollbackWindow) {
    const label = rollbackWindowLabel(window);
    requestApproval({
      operation: {
        action: "credential.rollback",
        targetType: "credential",
        targetId: window.credentialId,
        tenantId: window.tenantId,
        payload: { op: "rollback", fromVersion: window.fromVersion, toVersion: window.toVersion },
      },
      title: `回滚 ${label}`,
      targetLabel: `${label} · 目标版本 v${window.toVersion}`,
      impact: [
        rollbackImpact(window),
        `可回滚窗口将在 ${formatDateTime(window.expiresAt)} 到期；过期后前端不会继续执行。`,
        "执行成功后会重新读取服务配置中心和凭据状态。",
      ],
      recovery: "如回滚后服务仍异常，请重新发起凭据轮换并完成真实校验；审计记录会保留本次回滚原因。",
      execute: async (approvalId, reason) => {
        setBusyWindowId(window.id);
        try {
          await api(`/api/credentials/${window.credentialId}/rollback`, {
            method: "POST",
            body: JSON.stringify({
              version: window.toVersion,
              reason,
              idempotencyKey: makeActionKey("credential-rollback"),
              approvalId,
            }),
          });
          await onReload();
        } catch (failure) {
          throw new Error(saveErrorMessage(failure, "凭据回滚失败，窗口可能已过期或审批不匹配。"));
        } finally {
          setBusyWindowId(null);
        }
      },
    });
  }

  return <SectionCard icon={RefreshCw} title="可回滚窗口" description="只显示服务端确认的限时窗口；不要求操作者手填凭据编号或版本。">
    {unavailable && <InlineFeedback tone="warning">服务端暂未返回可选回滚窗口列表；当前只能等待安全角色从服务端列表选择，不能手动填写内部编号或版本。</InlineFeedback>}
    <DataTable
      ariaLabel="服务配置可回滚窗口"
      columns={columns}
      rows={rows}
      rowKey={(window) => window.id}
      empty={unavailable ? "回滚窗口列表尚未接入。" : "当前分区没有可执行的限时回滚窗口。"}
      rowActions={(window) => <button
        type="button"
        className="button secondary compact"
        disabled={!isRollbackWindowOpen(window) || busyWindowId === window.id}
        onClick={() => requestRollback(window)}
      >{busyWindowId === window.id ? "执行中…" : "进入回滚审批"}</button>}
    />
  </SectionCard>;
}

function PlatformConfigDrawer({ data, activeCard, itemKey, canManage, canRotate, onReload, onClose }: {
  data: PageData;
  activeCard: ServiceConfigCard;
  itemKey: string;
  canManage: boolean;
  canRotate: boolean;
  onReload: () => Promise<void>;
  onClose: () => void;
}) {
  const definition = platformDefinitions.find((item) => item.provider === itemKey);
  if (!definition) return null;
  return <V3Dialog
    title={`配置${definition.title}`}
    description="按官方平台资料完成应用配置并查看真实连接状态。"
    className="service-platform-dialog"
    wide
    onClose={onClose}
  >
    <div className="service-platform-dialog-body">
      <PlatformConfigForm
        definition={definition}
        metadata={data.platformConfigs.find((item) => item.provider === definition.provider)}
        centerItem={activeCard.items.find((item) => item.key === definition.provider)}
        canManage={canManage}
        canRotate={canRotate}
        onSaved={onReload}
      />
    </div>
  </V3Dialog>;
}

function AiConfigDialog({ data, activeCard, itemKey, canManage, canRotate, canManageCatalog, canTestService, onReload, requestApproval, onClose }: {
  data: PageData;
  activeCard: ServiceConfigCard;
  itemKey: string;
  canManage: boolean;
  canRotate: boolean;
  canManageCatalog: boolean;
  canTestService: boolean;
  onReload: () => Promise<void>;
  requestApproval: RequestApproval;
  onClose: () => void;
}) {
  const [discoveredCredential, setDiscoveredCredential] = useState({ apiKey: "", baseUrl: "" });
  const catalogServices = (activeCard.catalogServices ?? []).map((service) => service.key === "zhipu"
    ? { ...service, models: service.models.filter((model) => model.key.toLowerCase() !== "glm-ocr") }
    : service);
  const catalogByProvider = new Map(catalogServices.map((item) => [item.key, item]));
  const routing = data.credentials.find((item) => item.scope === "platform" && item.provider === "ai-runtime" && item.name === "ai-agent-routing");
  const selectedProvider = data.assistantDefault?.provider ?? null;
  const definition = aiProviderDefinitions.find((item) => itemKey === item.provider || itemKey.startsWith(`${item.provider}-`));
  if (!definition) return null;
  const providerCatalog = catalogByProvider.get(definition.provider);
  const hasProviderCatalog = Boolean(providerCatalog?.models.length);
  const providerCredential = data.credentials.find((item) => item.scope === "platform" && item.provider === definition.provider && item.name === "ai-provider-runtime");
  return <V3Dialog title={`配置${definition.title}`} description="在一个工作区完成模型账号、目录、连通测试与默认路线。" className="service-provider-dialog" wide onClose={onClose}>
    <div className="service-provider-dialog-body">
      {hasProviderCatalog && <div className="service-provider-summary" aria-label="AI 路线状态">
        <span><Bot size={16} aria-hidden /><small>默认模型</small><strong>{data.assistantDefault ? `${providerTitle(data.assistantDefault.provider)} · ${data.assistantDefault.model}` : "尚未设置默认模型"}</strong></span>
        <span><ShieldCheck size={16} aria-hidden /><small>默认路线</small><strong>{routing ? "已安全建立" : "尚未建立"}</strong></span>
      </div>}
      {!hasProviderCatalog && <section className="service-ai-onboarding" aria-label="模型登记">
        {canManageCatalog ? <AiCatalogManager catalog={catalogServices} provider={definition.provider} definition={definition} canDiscover={canTestService} hasSavedCredential={Boolean(providerCredential)} onCredentialDiscovered={setDiscoveredCredential} onSaved={onReload} /> : <InlineFeedback tone="danger">当前账号不能维护模型目录，请联系平台负责人或安全管理员完成首个模型登记。</InlineFeedback>}
      </section>}
      {hasProviderCatalog && <AiProviderForm
        definition={definition}
        catalogService={providerCatalog}
        credential={providerCredential}
        centerItem={activeCard.items.find((item) => item.key === definition.provider)}
        routingCredential={routing}
        selected={selectedProvider === definition.provider}
        canManage={canManage}
        canRotate={canRotate}
        canTestService={canTestService}
        initialApiKey={discoveredCredential.apiKey}
        initialBaseUrl={discoveredCredential.baseUrl}
        onSaved={onReload}
        requestApproval={requestApproval}
      />}
      {hasProviderCatalog && <details className="service-catalog-disclosure">
        <summary><span><WalletCards size={17} aria-hidden /><strong>{definition.title}模型目录</strong></span><small><span className="app-number">{providerCatalog?.models.length ?? 0}</span> 个模型</small></summary>
        <div>{providerCatalog ? <AiCatalogTable catalog={[providerCatalog]} /> : <InlineFeedback tone="danger">服务端尚未返回该服务商的模型目录，当前不会显示为可用。</InlineFeedback>}</div>
      </details>}
      {hasProviderCatalog && canManageCatalog && <AdvancedSettingsDisclosure title={`${definition.title}模型管理`} description="只维护当前服务商的模型能力、价格、预算和降级路线。"><AiCatalogManager catalog={catalogServices} provider={definition.provider} definition={definition} canDiscover={canTestService} hasSavedCredential={Boolean(providerCredential)} onCredentialDiscovered={setDiscoveredCredential} onSaved={onReload} /></AdvancedSettingsDisclosure>}
    </div>
  </V3Dialog>;
}

type AiCatalogEntry = { key: string; serviceKey: string; serviceTitle: string; model: AiCatalogModel };
const aiCapabilityLabels: Record<AiModelCapability, string> = {
  chat: "对话",
  vision: "视觉识别",
  tool_call: "工具调用",
  embedding: "向量检索",
  rerank: "结果重排",
};

function parseOptionalCatalogNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : null;
}

function formatMajorAmount(value: number | null | undefined) {
  if (value === null || value === undefined) return "";
  return String(value / 100);
}

function parseMajorAmountToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function formatPriceAmount(micros: number | null | undefined) {
  if (micros === null || micros === undefined) return "";
  return String(micros / 1000);
}

function parsePriceAmountToMicros(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 1000);
}

function AiCatalogManager({ catalog, provider, definition, canDiscover, hasSavedCredential, onCredentialDiscovered, onSaved }: { catalog: AiCatalogService[]; provider: AiProvider; definition: AiProviderDefinition; canDiscover: boolean; hasSavedCredential: boolean; onCredentialDiscovered: (value: { apiKey: string; baseUrl: string }) => void; onSaved: () => Promise<void> }) {
  const entries = useMemo<AiCatalogEntry[]>(() => catalog.filter((service) => service.key === provider).flatMap((service) => service.models.map((model) => ({
    key: `${service.key}::${model.key}`,
    serviceKey: service.key,
    serviceTitle: service.title,
    model,
  }))), [catalog, provider]);
  const [selectedKey, setSelectedKey] = useState(entries[0]?.key ?? "");
  const selected = entries.find((entry) => entry.key === selectedKey) ?? entries[0] ?? null;
  const [displayName, setDisplayName] = useState(selected?.model.name ?? "");
  const [status, setStatus] = useState<"active" | "disabled" | "retired">(selected?.model.status ?? "active");
  const [capabilities, setCapabilities] = useState<AiModelCapability[]>(selected?.model.capabilities ?? []);
  const [contextWindow, setContextWindow] = useState(selected?.model.contextWindow == null ? "" : String(selected.model.contextWindow));
  const [maxOutputTokens, setMaxOutputTokens] = useState(selected?.model.maxOutputTokens == null ? "" : String(selected.model.maxOutputTokens));
  const [currency, setCurrency] = useState<"CNY" | "USD">(selected?.model.currency ?? "CNY");
  const [inputTokenPrice, setInputTokenPrice] = useState(formatPriceAmount(selected?.model.inputTokenPriceMicros));
  const [outputTokenPrice, setOutputTokenPrice] = useState(formatPriceAmount(selected?.model.outputTokenPriceMicros));
  const [dailyTokenBudget, setDailyTokenBudget] = useState(selected?.model.dailyTokenBudget == null ? "" : String(selected.model.dailyTokenBudget));
  const [dailyCostBudget, setDailyCostBudget] = useState(formatMajorAmount(selected?.model.dailyCostBudgetMinor));
  const [monthlyCostBudget, setMonthlyCostBudget] = useState(formatMajorAmount(selected?.model.monthlyCostBudgetMinor));
  const [fallbackProvider, setFallbackProvider] = useState(selected?.model.fallbackProvider ?? "");
  const [fallbackModel, setFallbackModel] = useState(selected?.model.fallbackModel ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const activeFallbackProviders = useMemo(() => catalog
    .filter((service) => service.models.some((model) => model.status === "active" && `${service.key}::${model.key}` !== selected?.key))
    .map((service) => ({ value: service.key, label: service.title })), [catalog, selected?.key]);
  const fallbackModelOptions = useMemo(() => {
    if (!fallbackProvider) return [];
    const service = catalog.find((item) => item.key === fallbackProvider);
    return (service?.models ?? [])
      .filter((model) => model.status === "active" && `${service?.key}::${model.key}` !== selected?.key)
      .map((model) => ({ value: model.key, label: model.name }));
  }, [catalog, fallbackProvider, selected?.key]);
  const managementShapeReady = Boolean(selected
    && selected.model.capabilities !== undefined
    && selected.model.contextWindow !== undefined
    && selected.model.maxOutputTokens !== undefined);
  const amountUnit = currency === "USD" ? "美元" : "元";

  useEffect(() => {
    if (!selectedKey && entries[0]?.key) setSelectedKey(entries[0].key);
  }, [entries, selectedKey]);

  useEffect(() => {
    if (!selected) return;
    setDisplayName(selected.model.name);
    setStatus(selected.model.status ?? "active");
    setCapabilities(selected.model.capabilities ?? []);
    setContextWindow(selected.model.contextWindow == null ? "" : String(selected.model.contextWindow));
    setMaxOutputTokens(selected.model.maxOutputTokens == null ? "" : String(selected.model.maxOutputTokens));
    setCurrency(selected.model.currency ?? "CNY");
    setInputTokenPrice(formatPriceAmount(selected.model.inputTokenPriceMicros));
    setOutputTokenPrice(formatPriceAmount(selected.model.outputTokenPriceMicros));
    setDailyTokenBudget(selected.model.dailyTokenBudget == null ? "" : String(selected.model.dailyTokenBudget));
    setDailyCostBudget(formatMajorAmount(selected.model.dailyCostBudgetMinor));
    setMonthlyCostBudget(formatMajorAmount(selected.model.monthlyCostBudgetMinor));
    setFallbackProvider(selected.model.fallbackProvider ?? "");
    setFallbackModel(selected.model.fallbackModel ?? "");
    setError("");
    setSaved(false);
  }, [selected]);

  function selectFallbackProvider(provider: string) {
    setFallbackProvider(provider);
    const service = catalog.find((item) => item.key === provider);
    const firstActive = service?.models.find((model) => model.status === "active" && `${service.key}::${model.key}` !== selected?.key);
    setFallbackModel(firstActive?.key ?? "");
  }

  function optionalNumberInvalid(value: string) {
    return value.trim().length > 0 && parseOptionalCatalogNumber(value) === null;
  }

  function amountInvalid(value: string) {
    return value.trim().length > 0 && parseMajorAmountToMinor(value) === null;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    if (!managementShapeReady) { setError("服务端目录尚未返回模型能力和长度字段，前端不会用默认值覆盖。"); return; }
    if (!displayName.trim()) { setError("请填写模型展示名称。"); return; }
    if (reason.trim().length < 8) { setError("请填写至少 8 个字的调整原因，便于审计。"); return; }
    if (optionalNumberInvalid(contextWindow) || optionalNumberInvalid(maxOutputTokens) || optionalNumberInvalid(dailyTokenBudget)) {
      setError("上下文长度、最大输出长度和每日处理量预算只能填写正整数。");
      return;
    }
    if (amountInvalid(dailyCostBudget) || amountInvalid(monthlyCostBudget)) {
      setError(`费用预算请按${amountUnit}填写非负数字。`);
      return;
    }
    const inputPriceMicros = parsePriceAmountToMicros(inputTokenPrice);
    const outputPriceMicros = parsePriceAmountToMicros(outputTokenPrice);
    if (inputPriceMicros === null || outputPriceMicros === null) {
      setError(`请输入输入和输出单价，单位为${amountUnit}。`);
      return;
    }
    if (fallbackProvider && !fallbackModel) {
      setError("请选择服务端目录中的可用降级模型。");
      return;
    }
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api(`/api/service-config/ai/models/${encodeURIComponent(selected.serviceKey)}/${encodeURIComponent(selected.model.key)}`, {
        method: "PUT",
        body: JSON.stringify({
          displayName: displayName.trim(),
          status,
          capabilities,
          contextWindow: parseOptionalCatalogNumber(contextWindow),
          maxOutputTokens: parseOptionalCatalogNumber(maxOutputTokens),
          currency,
          inputTokenPriceMicros: inputPriceMicros,
          outputTokenPriceMicros: outputPriceMicros,
          dailyTokenBudget: parseOptionalCatalogNumber(dailyTokenBudget),
          dailyCostBudgetMinor: parseMajorAmountToMinor(dailyCostBudget),
          monthlyCostBudgetMinor: parseMajorAmountToMinor(monthlyCostBudget),
          fallbackProvider: fallbackProvider || null,
          fallbackModel: fallbackProvider ? fallbackModel : null,
          reason: reason.trim(),
          idempotencyKey: makeActionKey("ai-catalog-manage"),
        }),
      });
      setReason("");
      setSaved(true);
      await onSaved();
    } catch (failure) {
      setError(saveErrorMessage(failure, "AI 模型目录保存失败"));
    } finally {
      setBusy(false);
    }
  }

  if (!entries.length) return <AiCatalogCreateEmpty provider={provider} definition={definition} canDiscover={canDiscover} hasSavedCredential={hasSavedCredential} onCredentialDiscovered={onCredentialDiscovered} onSaved={onSaved} />;
  return <SectionCard icon={Settings2} title={`${providerTitle(provider)}模型管理`} description="这里只维护当前服务商的模型；其他服务商请返回清单，从对应行进入配置。">
    <form className="ai-catalog-manager" onSubmit={submit}>
      <label className="full">{providerTitle(provider)}模型<V3Select value={selected?.key ?? ""} onChange={setSelectedKey} ariaLabel={`${providerTitle(provider)}模型`} options={entries.map((entry) => ({ value: entry.key, label: entry.model.name }))} /></label>
      {!managementShapeReady && <InlineFeedback tone="warning">服务端目录尚未返回模型能力、上下文长度和最大输出长度，本表单不会以默认值覆盖这些字段。</InlineFeedback>}
      <label>展示名称<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required /></label>
      <label>状态<V3Select value={status} onChange={(value) => setStatus(value as "active" | "disabled" | "retired")} ariaLabel="模型状态" options={[
        { value: "active", label: "可用于正式服务" },
        { value: "disabled", label: "暂不使用" },
        { value: "retired", label: "已退役" },
      ]} /></label>
      <div className="ai-capability-evidence full"><span>已确认能力</span>{capabilities.length
        ? <div>{capabilities.map((capability) => <small key={capability}>{aiCapabilityLabels[capability]}</small>)}</div>
        : <strong>尚无真实验证结果</strong>}<em>能力由厂商元数据和真实连通验证更新，不在这里人工勾选。</em></div>
      <label>上下文长度<input type="number" min={1} step={1} value={contextWindow} onChange={(event) => setContextWindow(event.target.value)} placeholder="服务端未限制则留空" disabled={!managementShapeReady} /></label>
      <label>最大输出长度<input type="number" min={1} step={1} value={maxOutputTokens} onChange={(event) => setMaxOutputTokens(event.target.value)} placeholder="服务端未限制则留空" disabled={!managementShapeReady} /></label>
      <label>计价币种<V3Select value={currency} onChange={(value) => setCurrency(value as "CNY" | "USD")} ariaLabel="计价币种" options={[
        { value: "CNY", label: "人民币" },
        { value: "USD", label: "美元" },
      ]} /></label>
      <label>输入单价（每千处理单位，{amountUnit}）<input type="number" min={0} step="0.000001" value={inputTokenPrice} onChange={(event) => setInputTokenPrice(event.target.value)} /></label>
      <label>输出单价（每千处理单位，{amountUnit}）<input type="number" min={0} step="0.000001" value={outputTokenPrice} onChange={(event) => setOutputTokenPrice(event.target.value)} /></label>
      <label>每日处理量预算<input type="number" min={0} step={1} value={dailyTokenBudget} onChange={(event) => setDailyTokenBudget(event.target.value)} placeholder="不限制则留空" /></label>
      <label>每日费用预算（{amountUnit}）<input type="number" min={0} step="0.01" value={dailyCostBudget} onChange={(event) => setDailyCostBudget(event.target.value)} placeholder="不限制则留空" /></label>
      <label>每月费用预算（{amountUnit}）<input type="number" min={0} step="0.01" value={monthlyCostBudget} onChange={(event) => setMonthlyCostBudget(event.target.value)} placeholder="不限制则留空" /></label>
      <label>降级服务商<V3Select value={fallbackProvider} onChange={selectFallbackProvider} ariaLabel="降级服务商" options={[{ value: "", label: "不设置降级" }, ...activeFallbackProviders]} /></label>
      <label>降级模型{fallbackProvider
        ? <V3Select value={fallbackModel} onChange={setFallbackModel} ariaLabel="降级模型" disabled={!fallbackModelOptions.length} options={fallbackModelOptions} />
        : <span className="form-hint">先选择降级服务商；仅能从服务端 active 目录中选择。</span>}</label>
      <label className="full">调整原因<textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明本次价格、预算或降级调整依据" required /></label>
      {error && <div className="form-error full">{error}</div>}
      {saved && <div className="form-success full">模型目录已保存，列表会以服务端返回结果为准。</div>}
      <footer className="full"><small>目录保存不会自动启用服务；仍需连通测试、预算/降级证据和独立启用动作。</small><button className="button primary" disabled={busy || !managementShapeReady}><Save size={15} />{busy ? "正在保存…" : "保存目录设置"}</button></footer>
    </form>
  </SectionCard>;
}

function AiCatalogCreateEmpty({ provider, definition, canDiscover, hasSavedCredential, onCredentialDiscovered, onSaved }: { provider: AiProvider; definition: AiProviderDefinition; canDiscover: boolean; hasSavedCredential: boolean; onCredentialDiscovered: (value: { apiKey: string; baseUrl: string }) => void; onSaved: () => Promise<void> }) {
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState(definition.recommendedBaseUrl);
  const [discoveredModels, setDiscoveredModels] = useState<AiModelDiscoveryResponse["models"]>([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoveryError, setDiscoveryError] = useState("");
  const [manualMode, setManualMode] = useState(!canDiscover);
  const [model, setModel] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [capabilities, setCapabilities] = useState<AiModelCapability[]>([]);
  const [capabilitySource, setCapabilitySource] = useState<"provider" | "unreported">("unreported");
  const [currency, setCurrency] = useState<"CNY" | "USD">("CNY");
  const [inputTokenPrice, setInputTokenPrice] = useState("");
  const [outputTokenPrice, setOutputTokenPrice] = useState("");
  const [contextWindow, setContextWindow] = useState("");
  const [maxOutputTokens, setMaxOutputTokens] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const amountUnit = currency === "USD" ? "美元" : "元";

  async function discoverModels() {
    if (!hasSavedCredential && (!apiKey.trim() || !baseUrl.trim())) { setDiscoveryError("请先填写 API Key 和接口地址。"); return; }
    setDiscovering(true);
    setDiscoveryError("");
    try {
      const result = await api<AiModelDiscoveryResponse>(hasSavedCredential ? "/api/service-config/ai/discover-saved-models" : "/api/service-config/ai/discover-models", {
        method: "POST",
        body: JSON.stringify(hasSavedCredential ? { provider } : { provider, apiKey: apiKey.trim(), baseUrl: baseUrl.trim() }),
      });
      const chatModels = result.models.filter((item) => !(provider === "zhipu" && item.id.toLowerCase() === "glm-ocr"));
      setDiscoveredModels(chatModels);
      const first = chatModels[0];
      setModel(first?.id ?? "");
      setDisplayName(first?.name ?? first?.id ?? "");
      setCapabilities(first?.capabilities ?? []);
      setCapabilitySource(first?.capabilitySource ?? "unreported");
      setManualMode(false);
      if (!hasSavedCredential) onCredentialDiscovered({ apiKey: apiKey.trim(), baseUrl: baseUrl.trim() });
    } catch (failure) {
      setDiscoveredModels([]);
      setDiscoveryError(saveErrorMessage(failure, "没有读取到模型目录，请检查 API Key 和接口地址"));
    } finally {
      setDiscovering(false);
    }
  }

  function selectDiscoveredModel(value: string) {
    setModel(value);
    const selected = discoveredModels.find((item) => item.id === value);
    setDisplayName(selected?.name ?? value);
    setCapabilities(selected?.capabilities ?? []);
    setCapabilitySource(selected?.capabilitySource ?? "unreported");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const inputPriceMicros = parsePriceAmountToMicros(inputTokenPrice);
    const outputPriceMicros = parsePriceAmountToMicros(outputTokenPrice);
    if (!manualMode && discoveredModels.length === 0) { setError("请先使用 API Key 读取模型目录，或选择手动登记。"); return; }
    if (!model.trim() || !displayName.trim()) { setError("请选择或填写真实模型标识。"); return; }
    if (provider === "zhipu" && model.trim().toLowerCase() === "glm-ocr") { setError("glm-ocr 是独立票据 OCR 服务，不能登记到聊天模型目录。"); return; }
    if (inputPriceMicros === null || outputPriceMicros === null) { setError(`请输入输入和输出单价，单位为${amountUnit}。`); return; }
    if (contextWindow.trim() && parseOptionalCatalogNumber(contextWindow) === null) { setError("上下文长度只能填写正整数。"); return; }
    if (maxOutputTokens.trim() && parseOptionalCatalogNumber(maxOutputTokens) === null) { setError("最大输出长度只能填写正整数。"); return; }
    if (reason.trim().length < 8) { setError("请填写至少 8 个字的创建原因，便于审计。"); return; }
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api(`/api/service-config/ai/models/${encodeURIComponent(provider)}/${encodeURIComponent(model.trim())}`, {
        method: "PUT",
        body: JSON.stringify({
          displayName: displayName.trim(),
          status: "active",
          capabilities,
          contextWindow: parseOptionalCatalogNumber(contextWindow),
          maxOutputTokens: parseOptionalCatalogNumber(maxOutputTokens),
          currency,
          inputTokenPriceMicros: inputPriceMicros,
          outputTokenPriceMicros: outputPriceMicros,
          dailyTokenBudget: null,
          dailyCostBudgetMinor: null,
          monthlyCostBudgetMinor: null,
          fallbackProvider: null,
          fallbackModel: null,
          reason: reason.trim(),
          idempotencyKey: makeActionKey("ai-catalog-create"),
        }),
      });
      setModel("");
      setDisplayName("");
      setReason("");
      setSaved(true);
      await onSaved();
    } catch (failure) {
      setError(saveErrorMessage(failure, "AI 模型目录创建失败"));
    } finally {
      setBusy(false);
    }
  }

  const modelReady = manualMode || discoveredModels.length > 0;
  return <form className="ai-catalog-manager ai-catalog-create" onSubmit={submit}>
    <h3 className="sr-only">登记{providerTitle(provider)}模型</h3>
    <section className="ai-create-stage full" aria-labelledby="ai-connect-title">
      <header><b>1</b><span><strong id="ai-connect-title">连接模型服务</strong><small>填写服务商账号，系统自动读取可用模型。</small></span></header>
      {hasSavedCredential
        ? <div className="ai-discovery-row"><InlineFeedback tone="info">模型账号已安全保存，可直接读取服务商模型目录，无需再次填写 API Key。</InlineFeedback><button type="button" className="button primary" disabled={!canDiscover || discovering} onClick={() => { void discoverModels(); }}>{discovering ? "正在读取…" : "使用已保存配置读取模型"}</button></div>
        : <div className="ai-discovery-row">
        <label>API Key<input type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} minLength={8} autoComplete="new-password" placeholder="填写服务商提供的 API Key" disabled={!canDiscover} /></label>
        <label>接口地址<input type="url" value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} placeholder={definition.baseUrlPlaceholder} disabled={!canDiscover} /></label>
        <button type="button" className="button primary" disabled={!canDiscover || discovering || apiKey.trim().length < 8 || !baseUrl.trim()} onClick={() => { void discoverModels(); }}>{discovering ? "正在读取…" : "读取模型"}</button>
      </div>}
      <small className="ai-discovery-note">API Key 仅由控制面服务端验证，页面不会连接厂商，也不会回显密钥。</small>
      {!canDiscover && <InlineFeedback tone="info">当前账号没有连通测试权限，可先手动登记模型；密钥需由有权限的管理员保存。</InlineFeedback>}
      {discoveryError && <div className="ai-discovery-fallback"><InlineFeedback tone="warning">{discoveryError}</InlineFeedback><button type="button" className="button secondary" onClick={() => { setManualMode(true); setModel(""); setDisplayName(""); onCredentialDiscovered({ apiKey: apiKey.trim(), baseUrl: baseUrl.trim() }); }}>改为手动登记</button></div>}
    </section>
    {modelReady && <>
      <section className="ai-create-stage ai-create-model-stage full" aria-labelledby="ai-model-title">
        <header><b>2</b><span><strong id="ai-model-title">选择模型</strong><small>名称和能力直接采用厂商返回结果。</small></span></header>
        <div className="ai-create-fields">
          <label>{manualMode ? "模型标识" : "可用模型"}{manualMode
            ? <input value={model} onChange={(event) => { setModel(event.target.value); setDisplayName(event.target.value); }} placeholder="填写服务端真实模型标识" required />
            : <V3Select value={model} onChange={selectDiscoveredModel} ariaLabel="可用模型" options={discoveredModels.map((item) => ({ value: item.id, label: item.name === item.id ? item.id : `${item.name} · ${item.id}` }))} />}</label>
        </div>
        <div className="ai-capability-evidence"><span>能力状态</span>{capabilities.length
          ? <div>{capabilities.map((capability) => <small key={capability}>{aiCapabilityLabels[capability]}</small>)}</div>
          : <strong>待真实验证</strong>}<em>{capabilitySource === "provider" ? "由厂商目录返回" : "不会按名称猜测，验证前保持停用"}</em></div>
      </section>
      <section className="ai-create-stage full" aria-labelledby="ai-business-title">
        <header><b>3</b><span><strong id="ai-business-title">补充计价信息</strong><small>用于成本核算，可选参数默认收起。</small></span></header>
        <div className="ai-create-fields three-columns">
          <label>计价币种<V3Select value={currency} onChange={(value) => setCurrency(value as "CNY" | "USD")} ariaLabel="计价币种" options={[{ value: "CNY", label: "人民币" }, { value: "USD", label: "美元" }]} /></label>
          <label>输入单价（每千单位）<input type="number" min={0} step="0.000001" value={inputTokenPrice} onChange={(event) => setInputTokenPrice(event.target.value)} required /></label>
          <label>输出单价（每千单位）<input type="number" min={0} step="0.000001" value={outputTokenPrice} onChange={(event) => setOutputTokenPrice(event.target.value)} required /></label>
        </div>
        <details className="ai-catalog-optional">
          <summary>模型长度限制（可选）</summary>
          <div><label>上下文长度<input type="number" min={1} step={1} value={contextWindow} onChange={(event) => setContextWindow(event.target.value)} placeholder="不限制则留空" /></label><label>最大输出长度<input type="number" min={1} step={1} value={maxOutputTokens} onChange={(event) => setMaxOutputTokens(event.target.value)} placeholder="不限制则留空" /></label></div>
        </details>
        <label>登记说明<textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明模型来源、价格依据和使用目的" required /></label>
      </section>
      {error && <div className="form-error full">{error}</div>}
      {saved && <div className="form-success full">模型已登记，下一步保存账号并完成真实连通测试。</div>}
      <footer className="full"><small>新模型暂不启用，完成真实验证后再开放使用。</small><button className="button primary" disabled={busy}><Save size={15} />{busy ? "正在登记…" : "登记模型"}</button></footer>
    </>}
  </form>;
}

function AiCatalogTable({ catalog }: { catalog: AiCatalogService[] }) {
  const rows = catalog.flatMap((service) => service.models
    .filter((model) => !(service.key === "zhipu" && model.key.toLowerCase() === "glm-ocr"))
    .map((model) => ({ serviceTitle: service.title, ...model })));
  const columns: Array<DataTableColumn<AiCatalogModel & { serviceTitle: string }>> = [
    { key: "service", header: "服务商", width: "22%", render: (item) => <strong>{item.serviceTitle}</strong> },
    { key: "model", header: "模型", render: (item) => <span>{item.name}</span> },
    { key: "price", header: "价格口径", render: (item) => <span>{item.priceLabel}</span> },
    { key: "budget", header: "预算", render: (item) => <span>{item.budgetLabel ?? "预算待服务端确认"}</span> },
    { key: "fallback", header: "降级", render: (item) => <span>{item.fallbackLabel ?? "降级策略待服务端确认"}</span> },
    { key: "purpose", header: "适用场景", render: (item) => <span>{item.purpose}</span> },
  ];
  return <DataTable ariaLabel="AI 模型与价格目录" columns={columns} rows={rows} rowKey={(item) => `${item.serviceTitle}-${item.key}`} empty="服务端暂未返回模型目录。" />;
}

function providerTitle(provider: AiProvider) {
  return aiProviderDefinitions.find((item) => item.provider === provider)?.title ?? "模型服务";
}

function SmsConfigDialog({ data, activeCard, itemKey, canManage, canRotate, canVerifyCallback, canTestService, onReload, requestApproval, onClose }: { data: PageData; activeCard: ServiceConfigCard; itemKey: string; canManage: boolean; canRotate: boolean; canVerifyCallback: boolean; canTestService: boolean; onReload: () => Promise<void>; requestApproval: RequestApproval; onClose: () => void }) {
  const credential = data.credentials.find((item) => item.scope === "platform" && item.provider === "sms" && item.name === "sms-runtime");
  const wechatCredential = data.credentials.find((item) => item.scope === "platform" && item.provider === "web-wechat-login" && item.name === "web-wechat-runtime");
  const smsItem = activeCard.items.find((item) => item.key === "sms-runtime");
  const wechatItem = activeCard.items.find((item) => item.key === "web-wechat-runtime");
  const smsCallbackUrl = smsItem?.callbackUrl ?? "";
  const wechatCallbackUrl = wechatItem?.callbackUrl ?? "";
  const isWechat = itemKey === "web-wechat-runtime";
  return <V3Dialog
    title={isWechat ? "配置微信扫码登录" : "配置验证码短信"}
    description={isWechat ? "独立维护微信登录资料与授权回调，不借用短信状态。" : "在一个工作区完成短信账号、模板和真实发送测试；送达回执自动记录。"}
    className="service-provider-dialog"
    wide
    onClose={onClose}
  >
    <div className="service-provider-dialog-body">
      {isWechat
        ? <WebWechatLoginForm credential={wechatCredential} callbackUrl={wechatCallbackUrl} callbackStatus={wechatItem?.callbackStatus} canManage={canManage} canRotate={canRotate} onSaved={onReload} requestApproval={requestApproval} />
        : <SmsForm credential={credential} callbackUrl={smsCallbackUrl} callbackStatus={smsItem?.callbackStatus} canManage={canManage} canRotate={canRotate} canVerifyCallback={canVerifyCallback} canTestService={canTestService} onSaved={onReload} requestApproval={requestApproval} />}
    </div>
  </V3Dialog>;
}

export function ServiceConfigPage({ session, initialSection }: { session: SessionView; initialSection?: ServiceConfigSection | undefined }) {
  const [activeSection, setActiveSection] = useState<ServiceConfigSection>(() => initialSectionFromLocation(initialSection));
  const [approvalRequest, setApprovalRequest] = useState<ServiceApprovalRequest | null>(null);
  const [serviceEditor, setServiceEditor] = useState<{ section: ServiceConfigSection; key: string } | null>(null);
  const [serviceEvidence, setServiceEvidence] = useState<{ section: ServiceConfigSection; key: string; checking: boolean; checkResult: ServiceActionResult | null } | null>(null);
  const canManage = canManageServiceConfig(session);
  const canRotate = canRotateCredential(session);
  const canManageCatalog = canManage && hasAction(session, "service_config.manage");
  const canVerifyCallback = canManage && hasAction(session, "service_config.verify");
  const canTestService = canManage && hasAction(session, "service_config.test");
  const canRollback = canManage && hasAction(session, "credential.rollback");
  const canLoadSensitiveMetadata = (session.admin.role === "platform_owner" || session.admin.role === "security_admin") && hasAction(session, "credential.read_metadata");
  const pageLoader = useCallback(() => loadPageData(canLoadSensitiveMetadata, canRollback), [canLoadSensitiveMetadata, canRollback]);
  const remote = useRemote(pageLoader, (data) => data.center.cards.length === 0);
  const requestApproval = useCallback((request: ServiceApprovalRequest) => setApprovalRequest(request), []);

  function selectSection(section: ServiceConfigSection) {
    setActiveSection(section);
    if (typeof window !== "undefined") window.history.replaceState({}, "", `/service-config?section=${section}`);
  }

  async function openServiceEvidence(card: ServiceConfigCard, item: ServiceSubItem) {
    const target = { section: card.section, key: item.key, checking: false, checkResult: null };
    if (card.section !== "ai" || !hasSavedSecret(item) || item.callbackStatus === "verified") {
      setServiceEvidence(target);
      return;
    }
    if (!canTestService) {
      setServiceEvidence({ ...target, checkResult: { status: "failed", title: "当前账号不能执行连通测试", detail: "请联系平台负责人使用具备服务测试权限的账号操作。" } });
      return;
    }
    const provider = aiProviderDefinitions.find((definition) => item.key === definition.provider || item.key.startsWith(`${definition.provider}-`))?.provider;
    const model = provider
      ? card.catalogServices?.find((service) => service.key === provider)?.models.find((candidate) => candidate.status === "active")
      : null;
    if (!provider || !model) {
      setServiceEvidence({ ...target, checkResult: { status: "failed", title: "暂时无法执行连通测试", detail: "当前服务商还没有已启用的模型目录。请先在配置中登记真实模型名称、价格和用途。" } });
      return;
    }
    setServiceEvidence({ ...target, checking: true });
    try {
      const result = await api<RawVerificationResponse>("/api/service-config/ai/connectivity-test", {
        method: "POST",
        body: JSON.stringify({ provider, model: model.key, prompt: "请回复 OK，用于控制面上线前真实连通测试。", idempotencyKey: makeActionKey("ai-preflight-connectivity-test") }),
      });
      await remote.reload();
      setServiceEvidence((current) => current?.section === card.section && current.key === item.key
        ? { ...current, checking: false, checkResult: actionResult(result, "真实连通测试结果") }
        : current);
    } catch (failure) {
      setServiceEvidence((current) => current?.section === card.section && current.key === item.key
        ? { ...current, checking: false, checkResult: failedActionResult(failure, "真实连通测试失败，当前不会显示为已验证。") }
        : current);
    }
  }

  return <div className="page service-config-page">
    <PageHeader
      icon={Settings2}
      title="服务配置中心"
      meta={<span>平台应用、AI、短信统一入口</span>}
      navigation={<V3PageTabs tabs={serviceConfigTabs} active={activeSection} onChange={(value) => selectSection(value as ServiceConfigSection)} ariaLabel="服务配置页内视图" />}
    />
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? {
        title: "当前账号不能查看服务配置中心",
        description: "请联系平台负责人开通服务配置查看权限；页面不会用空数据冒充可用服务。",
      } : null}
      emptyHint="服务配置中心还没有返回任何服务类型。"
    >{(data) => {
      const cards = sectionOrder.map((section) => data.center.cards.find((card) => card.section === section)).filter((card): card is ServiceConfigCard => Boolean(card));
      const activeCard = cards.find((card) => card.section === activeSection) ?? cards[0];
      const activeDefinition = activeCard ? sectionDefinitions[activeCard.section] : sectionDefinitions.platform;
      const refreshAction = activeCard ? <div className="service-section-actions">
        <button type="button" className="button secondary" onClick={() => { void remote.reload(); }}><RefreshCw size={16} />刷新状态</button>
      </div> : null;
      const serviceColumns: Array<DataTableColumn<ServiceSubItem>> = canManage ? [
        {
          key: "name",
          header: "服务对象",
          width: "34%",
          render: (item) => <span className="service-config-object"><ServiceLogo item={item} fallback={activeDefinition.icon} /><span><strong>{item.title}</strong><small>{item.description}</small></span></span>,
        },
        { key: "status", header: "当前状态", width: "18%", render: (item) => <span className="service-config-state"><ServiceStatusBadge status={item.status ?? "not_configured"} /><small>{item.secretSummary}</small></span> },
        { key: "next", header: "下一步", render: (item) => <strong className="service-config-next-step">{serviceNextStep(item)}</strong> },
        { key: "updated", header: "最近更新", width: "150px", render: (item) => <span className="app-number">{item.updatedAt ?? "暂无"}</span> },
      ] : [
        {
          key: "name",
          header: "服务对象",
          width: "42%",
          render: (item) => <span className="service-config-object"><ServiceLogo item={item} fallback={activeDefinition.icon} /><span><strong>{item.title}</strong><small>{item.description}</small></span></span>,
        },
        { key: "availability", header: "可用性", render: (item) => <ServiceStatusBadge status={item.status ?? activeCard?.status ?? "not_configured"} /> },
        { key: "updated", header: "最后更新时间", width: "180px", render: (item) => <span className="app-number">{item.updatedAt ?? "暂无更新"}</span> },
      ];
      return activeCard ? <div
        id={`service-config-panel-${activeCard.section}`}
        role="tabpanel"
        aria-labelledby={`v3-page-tab-${activeCard.section}`}
        className="service-config-detail service-config-tab-panel"
      >
          <SectionCard icon={activeDefinition.icon} title={activeCard.title} meta={activeCard.section === "platform" ? <span className="service-platform-summary"><span className="app-number">{activeCard.items.filter((item) => item.status === "verified").length}</span> 个已可用 · <span className="app-number">{activeCard.items.filter((item) => item.status === "configured" || item.status === "failed").length}</span> 个配置中 · <span className="app-number">{activeCard.items.filter((item) => !item.status || item.status === "not_configured").length}</span> 个未配置</span> : <ServiceStatusBadge status={activeCard.status} />} action={refreshAction} variant="ledger">
            {activeCard.serviceUnavailable && <InlineFeedback tone="danger">{activeCard.serviceUnavailable}</InlineFeedback>}
            <DataTable
              ariaLabel={`${activeCard.title}服务对象`}
              columns={serviceColumns}
              rows={activeCard.items}
              rowKey={(item) => item.key}
              empty={activeCard.unconfiguredHint}
              {...(canManage ? {
                rowActions: (item: ServiceSubItem) => <span className="service-config-row-actions"><button type="button" className="button secondary compact" onClick={() => { void openServiceEvidence(activeCard, item); }}>上线检查</button><button type="button" className="button primary compact" onClick={() => setServiceEditor({ section: activeCard.section, key: item.key })}>配置</button></span>,
              } : {})}
            />
          </SectionCard>
          {activeCard.section === "ai" && <AiOcrConfigPanel canManage={canManageCatalog} canTestService={canTestService} onReload={remote.reload} />}
          {!canManage && <ReadonlyServiceBoundary />}
      </div> : null;
    }}</RemotePageState>
    {serviceEditor && remote.state.status === "ready" && (() => {
      const activeCard = remote.state.data.center.cards.find((card) => card.section === serviceEditor.section);
      if (!activeCard) return null;
      if (serviceEditor.section === "platform") return <PlatformConfigDrawer data={remote.state.data} activeCard={activeCard} itemKey={serviceEditor.key} canManage={canManage} canRotate={canRotate} onReload={remote.reload} onClose={() => setServiceEditor(null)} />;
      if (serviceEditor.section === "ai") return <AiConfigDialog data={remote.state.data} activeCard={activeCard} itemKey={serviceEditor.key} canManage={canManage} canRotate={canRotate} canManageCatalog={canManageCatalog} canTestService={canTestService} onReload={remote.reload} requestApproval={requestApproval} onClose={() => setServiceEditor(null)} />;
      return <SmsConfigDialog data={remote.state.data} activeCard={activeCard} itemKey={serviceEditor.key} canManage={canManage} canRotate={canRotate} canVerifyCallback={canVerifyCallback} canTestService={canTestService} onReload={remote.reload} requestApproval={requestApproval} onClose={() => setServiceEditor(null)} />;
    })()}
    {serviceEvidence && remote.state.status === "ready" && (() => {
      const evidenceCard = remote.state.data.center.cards.find((card) => card.section === serviceEvidence.section);
      const evidenceItem = evidenceCard?.items.find((item) => item.key === serviceEvidence.key);
      return evidenceCard && evidenceItem ? <ServiceItemEvidenceDialog
        card={evidenceCard}
        item={evidenceItem}
        automaticCheck={{ busy: serviceEvidence.checking, result: serviceEvidence.checkResult }}
        canTestService={canTestService}
        onConfigure={() => { setServiceEvidence(null); setServiceEditor({ section: evidenceCard.section, key: evidenceItem.key }); }}
        onClose={() => setServiceEvidence(null)}
        onReload={remote.reload}
      >
        {canRollback && <CredentialRollbackWindowsPanel section={evidenceCard.section} windows={remote.state.data.rollbackWindows} unavailable={remote.state.data.rollbackWindowsUnavailable} requestApproval={requestApproval} onReload={remote.reload} />}
      </ServiceItemEvidenceDialog> : null;
    })()}
    {approvalRequest && <ApprovalGateModal
      session={session}
      operation={approvalRequest.operation}
      title={approvalRequest.title}
      targetLabel={approvalRequest.targetLabel}
      impact={approvalRequest.impact}
      recovery={approvalRequest.recovery}
      execute={approvalRequest.execute}
      onClose={() => setApprovalRequest(null)}
      onExecuted={() => setApprovalRequest(null)}
    />}
  </div>;
}
